// Inspect card: combat OFF, the monocular in hand, trigger → a card about
// what the centre of the view is on (the picture freezes while it is open).
//
//   trigger   main.js tryFire: desktop LMB and the phone's ◎ button
//             (mobile.consumeInspectTap) at ANY zoom → Inspect.trigger, which
//             edge-latches the level-triggered fire (frame() clears it). VR:
//             either trigger off every panel (vrui.js → main.js vrPoint) opens
//             the card along that controller's ray (ctx.view() = its camera),
//             or at the head zoom window's reticle (the optic path); on the
//             card the vrui laser clicks (vrCard / vrClick).
//   target    resolveTarget, on the view main.js reports (ctx.view()):
//             optic band / VR zoom window → P = monoCam eye + dir × optic depth at the
//             reticle (512, 320 of the 1024×640 overlay; buildings + trees
//             included) → the terrain ray-march; below the band → the render
//             camera's centre ray: the building step test (≤ 300 m), then the
//             terrain ray-march → the far horizon (far-peak only).
//             resolveHits: building → trail → station → lift → water →
//             gazetteer → ground; every match is kept ("also here" chips).
//             3D furniture (FURN-3D, ctx.furniture.pick): the aim ray
//             through a drawn object's box leads with its amenity card; the
//             aimed point ≤ 1.5 m from one follows the building. Every
//             objects.json record likewise (objectRay: a mast against the
//             sky, a wall before the slope; objectNear: the aimed point on it).
//   station   standing in a station zone (or riding) with an enabled sign
//             row: the station hit leads the card and every enabled row is an
//             action button (stationzones' onShoot) — boarding by pointing,
//             no keyboard. F / G stay as desktop accelerators.
//   card      buildCard: header (icon, name, distance, bearing, ele, Δh) +
//             the rows of every INSPECT_FACETS entry that matches the entity
//             + actions (📍 beacon, 🗺 map, ⤳ beam) + provenance.
//             ONE model, three presenters: desktop DOM card beside the
//             reticle, phone bottom sheet, VR world plane (paintCard).
//   map       openAt(x, z, {pickR, poi, name, kind, ent, mppCss, client}): a tap / click on the
//             full Map (playermap onTapWorld → main.js). P = (x, ground, z),
//             eye = the player, resolveHits flat2d (no y gates; far buildings
//             from the register). The card sits beside the point (desktop) /
//             in the sheet with the Map panned clear (phone), no leader line,
//             no freeze, no pointer re-lock; 🗺 becomes 👁 Look (close the
//             Map, face it: main.js lookAt). Combat on: no beacon, no beam.
//             Closes with the Map, on a mode change, on a combat flip, on a
//             tap outside it (a tap on the Map then opens the next card).
//   pre-entry the same Map card BEFORE entry (ctx.mode() null, ctx.preEntry()
//             = the launch tap's mode): no player → no eye (no distance,
//             bearing, Δh; a blocked beam point steps out the shortest way),
//             no 👁 / 🗺; ⤳ Beam is the entry (ctx.enterAt, combat too);
//             opening it prefetches the near ground there (ctx.prefetch).
//   trails   (MAP-D) a Map tap resolves the nearest path line within the
//             pick radius (the TLM-only official segments too, R5) and an
//             amenity the Map shows at its zoom (opts.mppCss, the Amenities
//             chip); a tapped label's `ent` leads (route shield / name →
//             the route card, amenity icon, grade chip / trail name → path).
//             Path card: grade (R4: the official class sets the mark, the
//             OSM T-grade is the label) + swatch, official class, MTB scale,
//             width (oa), surface, visibility, incline, section / whole path +
//             walking time both ways, "On routes" (tap → route card). Route
//             card: plate + name, network, from → via → to, official km / ↑ /
//             ↓ / duration, MTB scale, loop, 🔗 website (new tab), "inside
//             this map" + walking time, stage; actions 🧭 Guide me (beacons.js
//             RouteGuide, R6) / Stop, ⇄ Reverse, ⏮ ⏭ stage, + the core 👁 📍.
//             Opening it highlights the route on the Map (ctx.mapRoute: the
//             others dim) and fits it; closing clears. Amenity card: kind,
//             name, height, tags; 🧭 Guide me (the plain waypoint) + 👁 📍.
//             Combat on: no Guide me (the waypoint is hidden then).
//   freeze    weapons.monoFreeze holds the optic picture while open (view).
//   telemetry net.inspect({kind, had, action, dist}) per open + per action
//             (route → trail, amenity → poi; guide → beacon, stage /
//             reverse → chip: the server's whitelist).
//
// Extending (wave 3): push a facet {id, match(ent), render(ent, env) → rows,
// had?(ent, rows) → bool, actions?(ent, env) → actions} onto INSPECT_FACETS. A row is {k, v} (+ dim,
// sw: mark bands, mtb: an MTB-scale colour dot, plate: {text, col [bg, fg,
// border]} a route plate, href: an external link (http/s, new tab), h:
// section heading, tap: entity to re-target). An action is
// {id, icon, label, run(env) → status string | Promise<string> | void,
// disabled?}. `env` = {ctx, target, ent, ui (the Inspect)}.
import * as THREE from 'three';
import { LABEL_ICON } from './scopehud.js';
import { MARK, MARK_COLOUR, OFFICIAL_MARK, gradeText, mtbClass, nearestPoly } from './trailmarks.js';
import { registryIndex, recordsNear, AMENITY, amenityLabel, amenityNear, routeFamily, routePlate, ROUTE_STYLE,
  NETWORK_LABEL } from './maplayers.js';
import { RouteGuide, GUIDE } from './beacons.js';
import { fmtDepth, fmtTempRow } from './water.js';
import { fetchAsset } from './assetq.js';
import { chargeAct } from './beamcharge.js';
import { t, lang } from './i18n.js';
import './i18n/player.js';

// ------------------------------------------------------------------ helpers
// a label map read at use time (never a string cached at module load):
// {k: () => t('…')} → an object whose k reads the current language's string
function lazyLabels(m) {
  const o = {};
  for (const [k, f] of Object.entries(m)) Object.defineProperty(o, k, { get: f, enumerable: true });
  return o;
}
const COMPASS8 = () => [t('player.insp.dir.n'), t('player.insp.dir.ne'), t('player.insp.dir.e'), t('player.insp.dir.se'),
  t('player.insp.dir.s'), t('player.insp.dir.sw'), t('player.insp.dir.w'), t('player.insp.dir.nw')];
// CRS: x = East, −z = North
export function bearingDeg(fx, fz, tx, tz) {
  let b = Math.atan2(tx - fx, -(tz - fz)) * 180 / Math.PI;
  if (b < 0) b += 360;
  return b;
}
export const compass8 = (deg) => COMPASS8()[Math.round(deg / 45) % 8];
export function fmtRange(m) {
  if (!Number.isFinite(m)) return '—';
  return m >= 10000 ? `${(m / 1000).toFixed(1)} km`
    : m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`;
}
const fmtInt = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const fmtSigned = (n) => `${n >= 0 ? '+' : '−'}${fmtInt(Math.abs(n))}`;

// Swiss walking time (DIN 33466, the SAC / Wanderwege rule on every yellow
// signpost): horizontal 4 km/h, 300 m/h up, 500 m/h down; t = max + min/2. → h
export function walkTimeH(lenM, ascM, descM) {
  const th = (lenM / 1000) / 4;
  const tv = ascM / 300 + descM / 500;
  return Math.max(th, tv) + Math.min(th, tv) / 2;
}
// signpost style: rounded to 5 min → "3 h 10 min" / "45 min"
export function fmtHours(h) {
  const m = Math.round((h * 60) / 5) * 5;
  const hh = Math.floor(m / 60), mm = m % 60;
  return hh ? `${hh} h${mm ? ` ${String(mm).padStart(2, '0')} min` : ''}` : `${mm} min`;
}

// LV95 → WGS84: swisstopo's approximate formulas (the published inverse of
// the polynomial mobile.js wgs84ToLv95 uses; ~0.1″ ≈ 3 m across CH)
export function lv95ToWgs84(E, N) {
  const y = (E - 2600000) / 1e6, x = (N - 1200000) / 1e6;
  const lam = 2.6779094 + 4.728982 * y + 0.791484 * y * x + 0.1306 * y * x * x - 0.0436 * y * y * y;
  const phi = 16.9023892 + 3.238272 * x - 0.270978 * y * y - 0.002528 * x * x
    - 0.0447 * y * y * x - 0.0140 * x * x * x;
  return { lat: phi * 100 / 36, lon: lam * 100 / 36 };
}
// game xz → LV95 E/N (meta.game_origin: x = E − e0, z = n1 − N); null when
// the world is not on the Swiss grid (gpsToGame's own limit)
export function gameToLv95(x, z, meta) {
  if (!meta || (meta.crs && meta.crs !== 'EPSG:2056')) return null;
  const o = meta.game_origin || meta.bbox;
  if (!o || o.e0 === undefined) return null;
  const n1 = o.n1 !== undefined ? o.n1 : meta.bbox && meta.bbox.n1;
  if (n1 === undefined) return null;
  return { E: o.e0 + x, N: n1 - z };
}

// GWR codes (BFS Merkmalskatalog: GKAT building category, GKLAS class)
export const GWR_CAT = lazyLabels({
  1010: () => t('player.insp.gwrCat.1010'), 1020: () => t('player.insp.gwrCat.1020'),
  1030: () => t('player.insp.gwrCat.1030'), 1040: () => t('player.insp.gwrCat.1040'),
  1060: () => t('player.insp.gwrCat.1060'), 1080: () => t('player.insp.gwrCat.1080'),
});
export const GWR_CLS = lazyLabels({
  1110: () => t('player.insp.gwrCls.1110'), 1121: () => t('player.insp.gwrCls.1121'), 1122: () => t('player.insp.gwrCls.1122'),
  1130: () => t('player.insp.gwrCls.1130'), 1211: () => t('player.insp.gwrCls.1211'), 1212: () => t('player.insp.gwrCls.1212'),
  1220: () => t('player.insp.gwrCls.1220'), 1230: () => t('player.insp.gwrCls.1230'), 1231: () => t('player.insp.gwrCls.1231'),
  1241: () => t('player.insp.gwrCls.1241'), 1242: () => t('player.insp.gwrCls.1242'), 1251: () => t('player.insp.gwrCls.1251'),
  1252: () => t('player.insp.gwrCls.1252'), 1261: () => t('player.insp.gwrCls.1261'), 1262: () => t('player.insp.gwrCls.1262'),
  1263: () => t('player.insp.gwrCls.1263'), 1264: () => t('player.insp.gwrCls.1264'), 1265: () => t('player.insp.gwrCls.1265'),
  1271: () => t('player.insp.gwrCls.1271'), 1272: () => t('player.insp.gwrCls.1272'), 1273: () => t('player.insp.gwrCls.1273'),
  1274: () => t('player.insp.gwrCls.1274'), 1275: () => t('player.insp.gwrCls.1275'), 1276: () => t('player.insp.gwrCls.1276'),
  1277: () => t('player.insp.gwrCls.1277'), 1278: () => t('player.insp.gwrCls.1278'),
});
const LIFT_LABEL = lazyLabels({
  funicular: () => t('player.insp.lift.funicular'), gondola: () => t('player.insp.lift.gondola'),
  cable_car: () => t('player.insp.lift.cableCar'), chair_lift: () => t('player.insp.lift.chairLift'),
  mixed_lift: () => t('player.insp.lift.mixedLift'), drag_lift: () => t('player.insp.lift.dragLift'),
  't-bar': () => t('player.insp.lift.tBar'), j_bar: () => t('player.insp.lift.jBar'),
  platter: () => t('player.insp.lift.platter'), rope_tow: () => t('player.insp.lift.ropeTow'),
  magic_carpet: () => t('player.insp.lift.magicCarpet'), zip_line: () => t('player.insp.lift.zipLine'),
});
export const KIND_LABEL = lazyLabels({
  building: () => t('player.insp.kind.building'), trail: () => t('player.insp.kind.trail'),
  peak: () => t('player.insp.kind.peak'), station: () => t('player.insp.kind.station'),
  lift: () => t('player.insp.kind.lift'), water: () => t('player.insp.kind.water'),
  poi: () => t('player.insp.kind.poi'), ground: () => t('player.insp.kind.ground'),
  route: () => t('player.insp.kind.route'), amenity: () => t('player.insp.kind.amenity'),
  object: () => t('player.insp.kind.object'),
});
const KIND_ICON = { building: '🏠', trail: '🥾', lift: '🚡', water: '🌊', ground: '⛰', station: '🚉', peak: '▲', poi: '📍',
  route: '🥾', amenity: '📍', object: '🧱' };
// the telemetry whitelist (server: kind building|trail|peak|station|lift|
// water|poi|ground, action open|beacon|map|beam|chip|near): the
// MAP-D kinds / actions report as their nearest whitelisted kin
const TEL_KIND = { route: 'trail', amenity: 'poi', object: 'poi' };
const TEL_ACTION = { guide: 'beacon', unguide: 'beacon', reverse: 'chip', next: 'chip', prev: 'chip', amguide: 'beacon' };
// swissTLM3D path object class (pathinfo oa) → words
const OA_LABEL = lazyLabels({ path1: () => t('player.insp.oa.path1'), path2: () => t('player.insp.oa.path2'),
  road3: () => t('player.insp.oa.road3'), road4: () => t('player.insp.oa.road4'), road6: () => t('player.insp.oa.road6'),
  road8: () => t('player.insp.oa.road8'), road10: () => t('player.insp.oa.road10'), spur: () => t('player.insp.oa.spur'),
  ferrata: () => t('player.insp.oa.ferrata'), frag: () => t('player.insp.oa.frag'), other: () => t('player.insp.oa.other') });
const BEL_LABEL = lazyLabels({ hard: () => t('player.insp.bel.hard'), natural: () => t('player.insp.bel.natural') });
const VIS_LABEL = lazyLabels({ excellent: () => t('player.insp.vis.excellent'), good: () => t('player.insp.vis.good'),
  intermediate: () => t('player.insp.vis.intermediate'), bad: () => t('player.insp.vis.bad'),
  horrible: () => t('player.insp.vis.horrible'), no: () => t('player.insp.vis.no') });
const ROUTE_WORD = lazyLabels({ hiking: () => t('player.insp.routeWord.hiking'), foot: () => t('player.insp.routeWord.foot'),
  mtb: () => t('player.insp.routeWord.mtb'), bicycle: () => t('player.insp.routeWord.bicycle') });
// "<word> route" as one phrase per kind (German compounds it); an unknown kind → "route"
const ROUTE_NAME = lazyLabels({ hiking: () => t('player.insp.routeName.hiking'), foot: () => t('player.insp.routeName.foot'),
  mtb: () => t('player.insp.routeName.mtb'), bicycle: () => t('player.insp.routeName.bicycle') });
const routeName = (r) => ROUTE_NAME[r.route] || t('player.insp.route.word');
const TAG_LABEL = lazyLabels({ backrest: () => t('player.insp.tag.backrest'), covered: () => t('player.insp.tag.covered'),
  drinking_water: () => t('player.insp.tag.drinkingWater'), summit_cross: () => t('player.insp.tag.summitCross'),
  historic: () => t('player.insp.tag.historic') });
// R12: a water facts source (water.json facts.src '<src>:<ref>') → its card
// label and attribution
const FACTS_BY = { bafu: 'BAFU', wikidata: 'Wikidata' };
const FACTS_ATT = lazyLabels({ bafu: () => ` · ${t('player.insp.src.bafuLakes')}`, wikidata: () => ' · Wikidata' });
// a safe external link (http / https only) → {href, host} | null
function safeLink(u) {
  try {
    const url = new URL(String(u));
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return { href: url.href, host: url.host.replace(/^www\./, '') + (url.pathname.length > 1 ? '/…' : '') };
  } catch { return null; }
}
const POI_LABEL = lazyLabels({
  peak: () => t('player.insp.poi.peak'), farpeak: () => t('player.insp.poi.farpeak'), pass: () => t('player.insp.poi.pass'),
  ridge: () => t('player.insp.poi.ridge'), village: () => t('player.insp.poi.village'), town: () => t('player.insp.poi.town'),
  city: () => t('player.insp.poi.city'), hamlet: () => t('player.insp.poi.hamlet'), suburb: () => t('player.insp.poi.suburb'),
  hotel: () => t('player.insp.poi.hotel'), hostel: () => t('player.insp.poi.hostel'), restaurant: () => t('player.insp.poi.restaurant'),
  cafe: () => t('player.insp.poi.cafe'), bar: () => t('player.insp.poi.bar'), alpine_hut: () => t('player.insp.poi.alpineHut'),
  hut: () => t('player.insp.poi.hut'), viewpoint: () => t('player.insp.poi.viewpoint'), museum: () => t('player.insp.poi.museum'),
  gallery: () => t('player.insp.poi.gallery'), attraction: () => t('player.insp.poi.attraction'), church: () => t('player.insp.poi.church'),
  chapel: () => t('player.insp.poi.chapel'), station: () => t('player.insp.poi.station'), lake: () => t('player.insp.poi.lake'),
  river: () => t('player.insp.poi.river'), hospital: () => t('player.insp.poi.hospital'),
});
// gazetteer kind → card/telemetry kind (the INSPECT-S whitelist vocabulary)
export function gazKind(k) {
  if (k === 'peak' || k === 'farpeak' || k === 'pass' || k === 'ridge') return 'peak';
  if (k === 'lake' || k === 'river') return 'water';
  if (k === 'station') return 'station';
  return 'poi';
}
const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const _infoOnce = new Set();
function infoOnce(key, msg) {
  if (_infoOnce.has(key)) return;
  _infoOnce.add(key);
  console.info(msg);
}

// ------------------------------------------------------------ objects
// Every objects.json record (bake.py bake_objects) is inspectable:
// - swissTLM3D (no src): points x/y/z/h (mast, utility, fountain, monument
//   …; `rz` the surveyed top the TLM_TOP_MAX / 2 m bound rejected), runs [[x, y, z]…]
//   + h (avalanche, wall, checkdam + fall, jetty, chute, lane …), rings
//   [[x, z]…] + y / gv / h (court, basin, berm, dam); `oa` indexes the doc's
//   `arts` [[layer, objektart]…] (absent on older bakes: those rows drop).
// - D1-OBJ (src 'osm' / 'rule'): barriers runs + h (+ st fence style, m
//   material, gaps), a pylon x/y/z/h/w + k, a power line runs + sup + kv/ci.
export const OBJ_LABEL = lazyLabels({ fence: () => t('player.insp.obj.fence'), guard_rail: () => t('player.insp.obj.guardRail'),
  hedge: () => t('player.insp.obj.hedge'), wall: () => t('player.insp.obj.wall'), coping: () => t('player.insp.obj.coping'),
  pylon: () => t('player.insp.obj.pylon'), powerline: () => t('player.insp.obj.powerline'), mast: () => t('player.insp.obj.mast'),
  tower: () => t('player.insp.obj.tower'), chimney: () => t('player.insp.obj.chimney'), windturbine: () => t('player.insp.obj.windturbine'),
  silo: () => t('player.insp.obj.silo'), utility: () => t('player.insp.obj.utility'), fountain: () => t('player.insp.obj.fountain'),
  monument: () => t('player.insp.obj.monument'), skijump: () => t('player.insp.obj.skijump'), waterfall: () => t('player.insp.obj.waterfall'),
  avalanche: () => t('player.insp.obj.avalanche'), dam: () => t('player.insp.obj.dam'), checkdam: () => t('player.insp.obj.checkdam'),
  jetty: () => t('player.insp.obj.jetty'), chute: () => t('player.insp.obj.chute'), lane: () => t('player.insp.obj.lane'),
  court: () => t('player.insp.obj.court'), basin: () => t('player.insp.obj.basin'), berm: () => t('player.insp.obj.berm'),
  generic: () => t('player.insp.obj.generic') });
const OBJ_ICON = { fence: '🚧', guard_rail: '🛣', hedge: '🌳', wall: '🧱', coping: '🧱', pylon: '🗼', powerline: '⚡',
  mast: '📡', tower: '🗼', chimney: '🏭', windturbine: '🌬', silo: '🏭', utility: '🚰', fountain: '⛲', monument: '🗿',
  skijump: '⛷', waterfall: '💧', avalanche: '🏔', dam: '🌊', checkdam: '🌊', jetty: '⚓', chute: '🛷', lane: '🏃',
  court: '🏟', basin: '💧', berm: '⛰' };
const objLabel = (o) => OBJ_LABEL[o.a] || OBJ_LABEL.generic;
const FENCE_LABEL = lazyLabels({ chain: () => t('player.insp.fence.chain'), electric: () => t('player.insp.fence.electric'),
  wire: () => t('player.insp.fence.wire'), metal: () => t('player.insp.fence.metal'), paling: () => t('player.insp.fence.paling'),
  board: () => t('player.insp.fence.board'), rail: () => t('player.insp.fence.rail'), knee: () => t('player.insp.fence.knee') });
const PYLON_LABEL = lazyLabels({ tower: () => t('player.insp.pylon.tower'), pole: () => t('player.insp.pylon.pole'),
  portal: () => t('player.insp.pylon.portal'), terminal: () => t('player.insp.pylon.terminal') });
// swissTLM3D OBJEKTART (bake.py OBJECT_ARCHETYPES keys) → words
const ART_EN = lazyLabels({ 'antenne klein': () => t('player.insp.art.antenneKlein'),
  'antenne gross': () => t('player.insp.art.antenneGross'), antenne: () => t('player.insp.art.antenne'),
  sendeturm: () => t('player.insp.art.sendeturm'), aussichtsturm: () => t('player.insp.art.aussichtsturm'),
  wasserturm: () => t('player.insp.art.wasserturm'), kuehlturm: () => t('player.insp.art.kuehlturm'),
  leuchtturm: () => t('player.insp.art.leuchtturm'), turm: () => t('player.insp.art.turm'), kamin: () => t('player.insp.art.kamin'),
  windturbine: () => t('player.insp.art.windturbine'), windkraftanlage: () => t('player.insp.art.windturbine'),
  silo: () => t('player.insp.art.silo'), wasserversorgung: () => t('player.insp.art.wasserversorgung'),
  brunnen: () => t('player.insp.art.brunnen'), quelle: () => t('player.insp.art.quelle'), denkmal: () => t('player.insp.art.denkmal'),
  bildstock: () => t('player.insp.art.bildstock'), triangulationspyramide: () => t('player.insp.art.triangulationspyramide'),
  wasserfall: () => t('player.insp.art.wasserfall'), sprungschanze: () => t('player.insp.art.sprungschanze'),
  schutzverbauung: () => t('player.insp.art.schutzverbauung'), gewaesserverbauung: () => t('player.insp.art.gewaesserverbauung'),
  lawinenverbauung: () => t('player.insp.art.lawinenverbauung'), trockenmauer: () => t('player.insp.art.trockenmauer'),
  stuetzmauer: () => t('player.insp.art.stuetzmauer'), mauer: () => t('player.insp.art.mauer'),
  stromleitung: () => t('player.insp.art.stromleitung'), scheibenstand: () => t('player.insp.art.scheibenstand'),
  hafensteg: () => t('player.insp.art.hafensteg'), bobbahn: () => t('player.insp.art.bobbahn'),
  laufbahn: () => t('player.insp.art.laufbahn'), staumauer: () => t('player.insp.art.staumauer'),
  schutzdamm: () => t('player.insp.art.schutzdamm'), wehr: () => t('player.insp.art.wehr'),
  wasserbecken: () => t('player.insp.art.wasserbecken'), sportplatz: () => t('player.insp.art.sportplatz'),
  hartbelagpiste: () => t('player.insp.art.hartbelagpiste'), 'rollfeld hartbelag': () => t('player.insp.art.rollfeldHartbelag') });
const artKey = (v) => String(v || '').toLowerCase().replace(/ü/g, 'ue').replace(/ä/g, 'ae').replace(/ö/g, 'oe');
// the TLM layer (bake: tlm_ stripped) → its feature class, 'bauten_versorgungsbaute_pkt' → 'Versorgungsbaute'
function layerWord(l) {
  const w = String(l || '').replace(/^(bauten|eo)_/, '').replace(/_(pkt|lin|ply)$/, '');
  return w ? w[0].toUpperCase() + w.slice(1) : '';
}
const OBJ_CELL = 32;
const OBJ_R = 1.0;            // 3D: the aimed point this near a line / a point object's side
const OBJ_RAY_M = 3000;       // the aim ray looks for objects this far (sky behind too)
const OBJ_PAD_RAD = 0.004;    // the aim ray's angular slack (÷ zoom): a 1 m mast at 300 m is hittable
const isPt = (o) => Number.isFinite(o.x) && !o.runs && !o.ring;
// a point object's half width: the drawn mast's base (objects.js mast w0), a pylon's w
function objHalfW(o) {
  if (o.a === 'mast') return Math.min(4, Math.max(1.2, (o.h || 0) * 0.07)) / 2;
  return o.w ? o.w / 2 : (o.a === 'pylon' ? 0.5 : 1);
}
// a ring as a closed [x, y, z] line, y = its own ground (gv) or the median
function ringLine(o) {
  const L = o.ring.map((p, i) => [p[0], Array.isArray(o.gv) && Number.isFinite(o.gv[i]) ? o.gv[i] : o.y, p[1]]);
  if (L.length) L.push(L[0]);
  return L;
}
function inRing(ring, x, z) {
  let ins = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j];
    if ((a[1] > z) !== (b[1] > z) && x < (b[0] - a[0]) * (z - a[1]) / (b[1] - a[1] + 1e-12) + a[0]) ins = !ins;
  }
  return ins;
}
function ringCentroid(ring) {
  let sx = 0, sz = 0;
  for (const p of ring) { sx += p[0]; sz += p[1]; }
  return { x: sx / ring.length, z: sz / ring.length };
}
const _objIdx = new WeakMap();
function objectIndex(doc) {
  let ix = _objIdx.get(doc);
  if (ix) return ix;
  ix = new Map();
  const add = (x, z, it) => {
    const k = Math.floor(x / OBJ_CELL) * 65536 + Math.floor(z / OBJ_CELL);
    let a = ix.get(k);
    if (!a) ix.set(k, a = []);
    if (a[a.length - 1] !== it) a.push(it);
  };
  for (const o of doc.objects || []) {
    if (!o || typeof o.a !== 'string') continue;
    if (isPt(o)) { add(o.x, o.z, o); continue; }
    if (Array.isArray(o.ring) && o.ring.length >= 3) {
      // every cell of its bbox: a tap / aim inside a 100 m court finds it
      let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
      for (const p of o.ring) { x0 = Math.min(x0, p[0]); x1 = Math.max(x1, p[0]); z0 = Math.min(z0, p[1]); z1 = Math.max(z1, p[1]); }
      for (let x = x0; x < x1 + OBJ_CELL; x += OBJ_CELL) for (let z = z0; z < z1 + OBJ_CELL; z += OBJ_CELL) add(Math.min(x, x1), Math.min(z, z1), o);
      continue;
    }
    for (const run of o.runs || []) {
      for (let i = 0; i + 1 < run.length; i++) {
        const a = run[i], b = run[i + 1], n = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[2] - a[2]) / (OBJ_CELL / 2)));
        for (let j = 0; j <= n; j++) add(a[0] + (b[0] - a[0]) * j / n, a[2] + (b[2] - a[2]) * j / n, o);
      }
    }
  }
  _objIdx.set(doc, ix);
  return ix;
}
function objectsAround(doc, x, z, r, out = new Set()) {
  const ix = objectIndex(doc);
  for (let cx = Math.floor((x - r) / OBJ_CELL); cx <= Math.floor((x + r) / OBJ_CELL); cx++) {
    for (let cz = Math.floor((z - r) / OBJ_CELL); cz <= Math.floor((z + r) / OBJ_CELL); cz++) {
      for (const o of ix.get(cx * 65536 + cz) || []) out.add(o);
    }
  }
  return out;
}
// the object at P: 3D = on it (xz ≤ OBJ_R off its line / side, y inside its
// height; inside a ring); flat (a Map tap) = the nearest within r, a point
// object (a pylon) before the line it carries. → {o, x, y, z, d} | null
export function objectNear(doc, P, flat = false, r = 0) {
  if (!doc || !Array.isArray(doc.objects)) return null;
  const R = flat ? Math.max(OBJ_R, r) : OBJ_R + 3;
  let best = null;
  for (const o of objectsAround(doc, P.x, P.z, R + 2)) {
    let h = null;
    if (isPt(o)) {
      const d = Math.max(0, Math.hypot(P.x - o.x, P.z - o.z) - (flat ? 0 : objHalfW(o)));
      if (!(flat ? d <= R : d <= OBJ_R && P.y >= o.y - 1 && P.y <= o.y + (o.h || 0) + 2)) continue;
      h = { o, x: o.x, y: o.y, z: o.z, d: d - 0.5 };   // a point wins a tie with its line
    } else if (Array.isArray(o.ring) && o.ring.length >= 3) {
      const n = nearestOnLine(ringLine(o), P.x, P.z);
      const ins = inRing(o.ring, P.x, P.z), d = ins ? 0 : n ? n.d : Infinity;
      const y0 = Math.min(o.y, ...(Array.isArray(o.gv) ? o.gv : []));
      if (!(flat ? d <= R : d <= OBJ_R && P.y >= y0 - 1 && P.y <= o.y + (o.h || 0) + 1)) continue;
      const c = ringCentroid(o.ring);
      h = { o, x: c.x, y: o.y, z: c.z, d };
    } else {
      if (o.a === 'powerline' && !flat) continue;     // a wire has no surface the aim lands on
      for (const run of o.runs || []) {
        const n = nearestOnLine(run, P.x, P.z);
        if (!n) continue;
        const ok = flat ? n.d <= R : n.d <= OBJ_R && P.y >= n.y - 1 && P.y <= n.y + (o.h || 1) + 1;
        if (ok && (!h || n.d < h.d)) h = { o, x: n.x, y: n.y, z: n.z, d: n.d };
      }
    }
    if (h && (!best || h.d < best.d)) best = h;
  }
  return best;
}
// the aim ray vs every object's drawn extent (a point: its upright cylinder
// over y .. y + h; a run / ring: its vertical wall quads ground .. top), t ≤
// maxT + 0.75 — what the eye hit, the sky behind it too (a slim mast is
// never "on" the ground the march finds behind it). Wires are not hit.
// → {o, t, x, y, z} | null (the nearest)
export function objectRay(doc, ray, maxT = Infinity, zoom = 1) {
  if (!doc || !Array.isArray(doc.objects) || !ray) return null;
  const T = Math.min(OBJ_RAY_M, maxT + 0.75);
  const hxz = Math.hypot(ray.dx, ray.dz);
  if (!(T > 0) || hxz < 1e-6) return null;
  const slack = OBJ_PAD_RAD / Math.max(1, zoom);
  const cand = new Set();
  const step = OBJ_CELL / 2, n = Math.ceil(T * hxz / step);
  for (let i = 0; i <= n; i++) {
    const t = Math.min(T, (i * step) / hxz);
    objectsAround(doc, ray.ox + ray.dx * t, ray.oz + ray.dz * t, step + 4, cand);
  }
  let best = null;
  const take = (o, t, x, y, z) => { if (t >= 0 && t <= T && (!best || t < best.t)) best = { o, t, x, y, z }; };
  // the ray crossing the vertical quad a → b (each [x, y-ground, z]) between ground and ground + h
  const seg = (o, a, b, top) => {
    const ex = b[0] - a[0], ez = b[2] - a[2], L = Math.hypot(ex, ez);
    const den = ray.dx * ez - ray.dz * ex;
    if (L < 1e-6 || Math.abs(den) < 1e-9) return;
    const ax = a[0] - ray.ox, az = a[2] - ray.oz;
    const t = (ax * ez - az * ex) / den, u = (ax * ray.dz - az * ray.dx) / den;
    const pad = t * slack;
    if (t < 0 || u < -pad / L || u > 1 + pad / L) return;
    const uc = Math.max(0, Math.min(1, u)), g = a[1] + (b[1] - a[1]) * uc, y = ray.oy + ray.dy * t;
    if (y >= g - 0.3 - pad && y <= top(g) + 0.3 + pad) take(o, t, a[0] + ex * uc, g, a[2] + ez * uc);
  };
  for (const o of cand) {
    if (o.a === 'powerline') continue;
    const h = o.h || 0;
    if (isPt(o)) {
      const cx = o.x - ray.ox, cz = o.z - ray.oz;
      const ts = (cx * ray.dx + cz * ray.dz) / (hxz * hxz);    // closest approach in xz
      const d = Math.hypot(ray.ox + ray.dx * ts - o.x, ray.oz + ray.dz * ts - o.z);
      const r = objHalfW(o) + Math.max(0, ts) * slack;
      if (ts < 0 || d > r) continue;
      const tIn = Math.max(0, ts - Math.sqrt(r * r - d * d) / hxz);
      for (const t of [tIn, ts]) {
        const y = ray.oy + ray.dy * t;
        if (y >= o.y - 0.5 && y <= o.y + h + 0.5 + t * slack) { take(o, t, o.x, o.y, o.z); break; }
      }
    } else if (Array.isArray(o.ring) && o.ring.length >= 3) {
      const L = ringLine(o), top = o.y + h;
      const c = ringCentroid(o.ring);
      const b0 = best;
      for (let i = 0; i + 1 < L.length; i++) seg(o, L[i], L[i + 1], () => top);
      if (best !== b0 && best.o === o) { best.x = c.x; best.y = o.y; best.z = c.z; }
    } else {
      for (const run of o.runs || []) for (let i = 0; i + 1 < run.length; i++) seg(o, run[i], run[i + 1], (g) => g + (h || 1));
    }
  }
  return best;
}
// a pylon's voltage: the highest kV of a power line through it (pylons carry none)
function pylonKv(doc, o) {
  let kv = null;
  for (const l of objectsAround(doc, o.x, o.z, 1)) {
    if (l.a !== 'powerline' || !l.kv) continue;
    if ((l.runs || []).some((run) => run.some((p) => Math.abs(p[0] - o.x) < 0.5 && Math.abs(p[2] - o.z) < 0.5))) kv = Math.max(kv || 0, l.kv);
  }
  return kv;
}
const kvText = (kv) => `${kv >= 10 ? fmtInt(kv) : kv} kV`;
// bake.py writes src 'osm' / 'rule' on its own records and no src on swissTLM3D ones
const objIsTlm = (o) => o.src === 'tlm' || !o.src;
// a height the bake assumed (class default / pole default), not measured or tagged
const TYPICAL_H = new Set(['default', 'pole']);
// where the height came from, worded per record shape (bake.py tlm_point_height,
// the run median, the ring top; barrier_height / power_height on OSM)
function heightFrom(o) {
  if (o.hsrc === 'tlm') {
    return isPt(o) ? t('player.insp.hfrom.tlmPoint')
      : o.ring ? t('player.insp.hfrom.tlmOutline') : t('player.insp.hfrom.tlmLine');
  }
  if (o.hsrc === 'osm') return t('player.insp.hfrom.osm');
  if (o.hsrc === 'pole') return t('player.insp.hfrom.pole');
  if (o.hsrc === 'rule') return t('player.insp.hfrom.rule');
  if (o.hsrc === 'default') return objIsTlm(o) ? t('player.insp.hfrom.defaultTlm') : t('player.insp.hfrom.defaultOsm');
  return null;
}
const fmtM = (v) => `${fmtInt(v)} m`;
// "<height> a.s.l." (the height already formatted with its unit)
const asl = (h) => t('player.insp.val.asl', { h });
export function objectRows(e, doc = null) {
  const o = e.data.o, rows = [];
  const tlm = objIsTlm(o);
  const art = tlm && doc && Array.isArray(doc.arts) && Number.isInteger(o.oa) ? doc.arts[o.oa] : null;
  const gloss = art ? ART_EN[artKey(art[1])] : null;
  rows.push({ k: t('player.insp.row.type'), v: objLabel(o) + (o.a === 'pylon' && PYLON_LABEL[o.k] ? ` — ${PYLON_LABEL[o.k]}` : '')
    + (gloss && gloss.toLowerCase() !== objLabel(o).toLowerCase() ? ` — ${gloss}` : '') });
  if (art && art[1]) rows.push({ k: t('player.insp.row.tlmClass'), v: art[1] + (layerWord(art[0]) ? ` (${layerWord(art[0])})` : '') });
  if (o.n && o.n !== e.name) rows.push({ k: t('player.insp.row.name'), v: String(o.n) });
  if (o.a === 'fence') {
    if (o.st && FENCE_LABEL[o.st]) rows.push({ k: t('player.insp.row.fenceType'), v: FENCE_LABEL[o.st] });
    if (o.m) rows.push({ k: t('player.insp.row.material'), v: String(o.m) });
  }
  if (o.a === 'wall' && o.m) rows.push({ k: t('player.insp.row.material'), v: String(o.m) });
  if (o.a === 'coping') rows.push({ k: t('player.insp.row.coping'), v: t('player.insp.obj.copingSide'), dim: true });
  let L = 0, gLo = Infinity, gHi = -Infinity;
  for (const run of o.runs || []) {
    for (let i = 0; i < run.length; i++) {
      gLo = Math.min(gLo, run[i][1]); gHi = Math.max(gHi, run[i][1]);
      if (i) L += Math.hypot(run[i][0] - run[i - 1][0], run[i][2] - run[i - 1][2]);
    }
  }
  if (o.a === 'powerline') {
    const kv = o.kv || null;
    rows.push({ k: t('player.insp.row.voltage'), v: kv ? kvText(kv) : t('player.insp.val.notTagged'), ...(kv ? {} : { dim: true }) });
    if (o.sf) rows.push({ k: t('player.insp.row.tlmVoltage'), v: String(o.sf) });
    if (o.ci) rows.push({ k: t('player.insp.row.circuits'), v: String(o.ci) });
    let n = 0;
    for (const run of o.runs || []) n += run.length;
    rows.push({ k: t('player.insp.row.supports'), v: String(n) });
    rows.push({ k: t('player.insp.row.length'), v: fmtRange(L) });
  } else {
    if (o.a === 'pylon') {
      const kv = doc ? pylonKv(doc, o) : null;
      if (kv) rows.push({ k: t('player.insp.row.voltage'), v: kvText(kv) });
      if (o.lv) rows.push({ k: t('player.insp.row.circuits'), v: String(o.lv) });
    }
    if (L > 0) rows.push({ k: t('player.insp.row.length'), v: fmtRange(L) });
    if (Array.isArray(o.gaps) && o.gaps.length) rows.push({ k: t('player.insp.row.openings'), v: String(o.gaps.length) });
    if (Array.isArray(o.ring) && o.ring.length >= 3) {
      let A = 0, P = 0;
      for (let i = 0, j = o.ring.length - 1; i < o.ring.length; j = i++) {
        A += o.ring[j][0] * o.ring[i][1] - o.ring[i][0] * o.ring[j][1];
        P += Math.hypot(o.ring[i][0] - o.ring[j][0], o.ring[i][1] - o.ring[j][1]);
      }
      rows.push({ k: t('player.insp.row.footprint'), v: t('player.insp.obj.footprint', { area: fmtInt(Math.abs(A) / 2), outline: fmtRange(P) }) });
    }
    if (o.h) rows.push({ k: t('player.insp.row.height'), v: TYPICAL_H.has(o.hsrc) ? t('player.insp.obj.heightTypical', { h: o.h }) : `${o.h} m` });
    const hf = o.h ? heightFrom(o) : null;
    if (hf) rows.push({ k: t('player.insp.row.heightFrom'), v: hf, dim: TYPICAL_H.has(o.hsrc) });
    // the surveyed point top the bake rejected (bake.py tlm_point_height): said, never hidden
    if (Number.isFinite(o.rz) && Number.isFinite(o.y)) {
      const dz = o.rz - o.y;
      const why = dz < 2 ? t('player.insp.obj.surveyAtGround')
        : t('player.insp.obj.implausibleFor', { what: art ? t('player.insp.obj.anArt', { art: art[1] }) : t('player.insp.obj.itsClass') });
      rows.push({ k: t('player.insp.row.surveyTop'), v: t('player.insp.obj.surveyTop', { top: fmtM(o.rz), dz: dz.toFixed(1), why }), dim: true });
    }
    // elevations: a point / ring stands on y (a ring's median ground, its outline's range), a run on its vertices
    if (o.fall && Number.isFinite(o.fall.top)) {
      rows.push({ k: t('player.insp.row.fallCrest'), v: asl(fmtM(o.fall.top)) });
      if (Number.isFinite(o.fall.base)) rows.push({ k: t('player.insp.row.fall'), v: t('player.insp.obj.fallTo', { h: (o.fall.top - o.fall.base).toFixed(1), base: fmtM(o.fall.base) }) });
    } else if (Number.isFinite(o.y) && !o.runs) {
      const gv = Array.isArray(o.gv) ? o.gv.filter(Number.isFinite) : [];
      const lo = gv.length ? Math.min(...gv) : o.y, hi = gv.length ? Math.max(...gv) : o.y;
      rows.push({ k: t('player.insp.row.base'), v: hi - lo >= 1 ? asl(`${fmtInt(lo)}–${fmtM(hi)}`) : asl(fmtM(o.y)) });
      if (o.h) rows.push({ k: t('player.insp.row.top'), v: asl(fmtM(o.y + o.h)) });
    } else if (gLo <= gHi) {
      rows.push({ k: t('player.insp.row.ground'), v: gHi - gLo >= 1 ? asl(`${fmtInt(gLo)}–${fmtM(gHi)}`) : asl(fmtM(gLo)) });
    }
  }
  if (tlm) {
    const rel = doc && typeof doc.source === 'string' ? (/(\d{4}-\d{2})/.exec(doc.source) || [])[1] : null;
    rows.push({ k: t('player.insp.row.source'), v: rel ? t('player.insp.obj.tlmRelease', { rel }) : 'swissTLM3D (swisstopo)', dim: true });
  } else if (o.src === 'osm' && o.id) {
    rows.push({ k: 'OSM', v: `${o.a === 'pylon' ? 'node' : 'way'} ${o.id}`, dim: true });
  } else if (o.src === 'rule') {
    rows.push({ k: t('player.insp.row.drawnBy'), v: t('player.insp.obj.ruleDrawn'), dim: true });
  }
  return rows;
}

// ------------------------------------------------------------ resolution
const TRAIL_R = 8;          // trailAt radius at P
const LIFT_R = 15;          // horizontal distance P → line
const BLD_TOL = 1.2;        // a facade hit lands just outside its ring
const LABEL_PX = 40;        // a label this near the reticle is "aimed at"
const FAR_M = 1500;         // beyond this, an aimed-at summit leads the card
const MAX_CHIPS = 4;

function nearestOnTrail(t, x, z) {
  const p = t.pts;
  let bx = p[0], bz = p[1], bd = Infinity;
  for (let i = 0; i + 1 < p.length; i += 2) {
    const d = (p[i] - x) ** 2 + (p[i + 1] - z) ** 2;
    if (d < bd) { bd = d; bx = p[i]; bz = p[i + 1]; }
  }
  return { x: bx, z: bz };
}

// nearest point of a 3D polyline [[x,y,z]…] to (x, z), horizontally
function nearestOnLine(pts, x, z) {
  let best = null, bd = Infinity;
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i], b = pts[i + 1];
    const dx = b[0] - a[0], dz = b[2] - a[2];
    const t = Math.max(0, Math.min(1, ((x - a[0]) * dx + (z - a[2]) * dz) / (dx * dx + dz * dz + 1e-9)));
    const px = a[0] + dx * t, pz = a[2] + dz * t;
    const d = Math.hypot(x - px, z - pz);
    if (d < bd) { bd = d; best = { x: px, y: a[1] + (b[1] - a[1]) * t, z: pz, d }; }
  }
  return best;
}

function centroidOf(fp) {
  const r = fp && fp[0];
  if (!r || !r.length) return null;
  let sx = 0, sz = 0;
  for (const p of r) { sx += p[0]; sz += p[1]; }
  return { x: sx / r.length, z: sz / r.length };
}

// the registry record of a collision item, by its uuid (the
// buildings/registry.json key every building collider carries — one without
// it is a broken bake and throws); not in the register → none
export function registryRecord(reg, item) {
  if (!reg || !reg.buildings || !item) return { rec: null, how: 'none' };
  if (!item.uuid) throw new Error('bake building collider uuid missing: rebuild this world (Build)');
  const rec = reg.buildings[item.uuid];
  return rec ? { rec, how: 'uuid' } : { rec: null, how: 'none' };
}

// the station sign's enabled rows (stationzones._rows: {text, dim, onShoot})
function stationRows(zs) {
  return zs && zs._rows ? zs._rows.filter((r) => !r.dim && r.onShoot) : [];
}
// a station entity whose sign rows these are: the ride lead while riding,
// else the zone the player stands in
function ownsStationRows(zs, e) {
  if (e.kind !== 'station') return false;
  if (zs.riding) return !!e.data.ride;
  return !!(zs.zone && e.data.zone === zs.zone);
}
// the card leads with the station whenever its sign has something to do
export function stationLead(ctx, hits, eye) {
  const zs = ctx.zones;
  if (!zs || !stationRows(zs).length) return hits;
  if (zs.riding) {
    const r = zs.riding;
    hits.unshift({ kind: 'station', name: r.dest ? `→ ${r.dest}` : t('player.insp.riding'), x: eye.x, y: eye.y, z: eye.z, data: { ride: true } });
    return hits;
  }
  const i = hits.findIndex((h) => ownsStationRows(zs, h));
  if (i > 0) hits.unshift(hits.splice(i, 1)[0]);
  return hits;
}

// P → every entity there, in priority order (the first leads the card, the
// rest become chips). opts: {zoom, far (no surface: horizon point),
// labels: the view's label features (or null), reg: building registry | null,
// flat2d + pickR: a Map tap — no y gates, radii by the map's pick radius}
export function resolveHits(ctx, P, opts = {}) {
  const hits = [];
  const sky = !!opts.sky;
  const zoom = Math.max(1, opts.zoom || 1);
  // flat2d (a map tap): P.y is only the ground, so the height gates relax and
  // the radii follow the map's pick radius (m, ~16–22 CSS px at its zoom)
  const flat = !!opts.flat2d;
  const pickR = Math.max(1, opts.pickR || 0);
  const ground = (x, z) => (ctx.rangeGround ? ctx.rangeGround(x, z) : null);
  // FURN-3D: a drawn furniture object in the 3D view (furniture.js pick):
  // the aim ray through its box leads the card (it is what the eye hit, sky
  // behind it too); the aimed point ≤ 1.5 m from one comes after a building
  const fh = !flat && ctx.furniture && ctx.furniture.pick && ctx.amenities
    ? ctx.furniture.pick(opts.ray || null, sky ? null : P, sky ? Infinity : opts.dist ?? Infinity) : null;
  const fp = fh ? ctx.amenities[fh.i] : null;
  const fEnt = fp ? { kind: 'amenity', name: fp.name || '', x: fp.x, y: fh.y ?? fp.y ?? P.y, z: fp.z,
    data: { am: fp, ai: fh.i } } : null;
  // an objects.json record the aim ray passes through (a mast against the
  // sky, a wall before the slope behind it) leads the card like furniture
  const od = ctx.objects ? ctx.objects() : null;
  const orh = !flat && od && opts.ray ? objectRay(od, opts.ray, sky ? Infinity : opts.dist ?? Infinity, zoom) : null;
  const oEnt = orh ? { kind: 'object', name: orh.o.n ? String(orh.o.n) : '', x: orh.x, y: orh.y, z: orh.z,
    data: { o: orh.o, doc: od } } : null;

  if (!sky) {
    // building
    const bTol = flat ? Math.max(BLD_TOL, Math.min(pickR, 15)) : BLD_TOL;
    const b = ctx.collider && ctx.collider.buildingAt ? ctx.collider.buildingAt(P.x, P.z, bTol) : null;
    if (b && (flat || (P.y >= b.y0 - 1.5 && P.y <= b.y1 + 3))) {
      const { rec, how } = registryRecord(opts.reg, b);
      const c = rec && typeof rec.x === 'number' ? { x: rec.x, z: rec.z } : (centroidOf(b.fp) || P);
      const name = rec && (rec.name || (rec.osm && rec.osm.name) || (rec.gwr && rec.gwr.label));
      hits.push({ kind: 'building', name: name || '', x: c.x, y: b.y0, z: c.z,
        data: { item: b, rec, how, top: b.y1 } });
    } else if (flat && opts.reg) {
      // the map: no resident footprint there (far from the player) → the
      // nearest registry record (its own point; no footprint, no height)
      const n = recordsNear(registryIndex(opts.reg), P.x, P.z, Math.max(BLD_TOL * 4, Math.min(pickR, 40)));
      if (n) {
        const rec = n.e.rec;
        const name = rec.name || (rec.osm && rec.osm.name) || (rec.gwr && rec.gwr.label);
        hits.push({ kind: 'building', name: name || '', x: n.e.x, y: ground(n.e.x, n.e.z) ?? P.y, z: n.e.z,
          data: { item: null, rec, how: 'registry', top: null } });
      }
    }
    // an amenity the map shows at its zoom (MAP-D, the Amenities chip on)
    const chips = ctx.chips ? ctx.chips() : null;
    if (flat && ctx.amenities && (!chips || chips.amen)) {
      const a = amenityNear(ctx.amenities, P.x, P.z, Math.min(pickR, 40), opts.mppCss ?? null);
      if (a) {
        hits.push({ kind: 'amenity', name: a.p.name || '', x: a.p.x, y: a.p.y ?? ground(a.p.x, a.p.z) ?? P.y, z: a.p.z,
          data: { am: a.p, ai: a.i } });
      }
    }
    if (fEnt && fh.how === 'near') hits.push(fEnt);
    // any objects.json record (3D: the aimed point on it, unless the ray
    // already hit it; a Map tap: the nearest, power lines too)
    const oh = od ? objectNear(od, P, flat, flat ? Math.min(pickR, 20) : 0) : null;
    if (oh && !(orh && orh.o === oh.o)) {
      const oo = oh.o;
      hits.push({ kind: 'object', name: oo.n ? String(oo.n) : '', x: oh.x, y: Number.isFinite(oh.y) ? oh.y : P.y, z: oh.z,
        data: { o: oo, doc: od } });
    }
    // trail (path family, graded or not); the map has no height → no y gate:
    // the nearest path line within the pick radius, the TLM-only official
    // segments (R5: drawn + tappable, never walkable) included
    let t = null, tp = null;
    if (flat && ctx.roadnet && Array.isArray(ctx.roadnet.trails)) {
      const r = Math.max(TRAIL_R, Math.min(pickR, 40));
      const n = nearestPoly(ctx.roadnet.trails, P.x, P.z, r);
      const nx = ctx.pathExtra ? nearestPoly(ctx.pathExtra, P.x, P.z, r) : null;
      const b = nx && (!n || nx.d < n.d - 1) ? nx : n;
      if (b) { t = b.item; tp = { x: b.x, z: b.z }; }
    } else if (ctx.roadnet && ctx.roadnet.trailAt) {
      t = flat ? ctx.roadnet.trailAt(P.x, P.y, P.z, Math.max(TRAIL_R, Math.min(pickR * 0.6, 40)), 1e5)
        : ctx.roadnet.trailAt(P.x, P.y, P.z, TRAIL_R);
    }
    if (t) {
      const s = tp || nearestOnTrail(t, P.x, P.z);
      const y = ground(s.x, s.z);
      hits.push({ kind: 'trail', name: t.name || '', x: s.x, y: y ?? P.y, z: s.z, data: { trail: t } });
    }
    // station zone (the stationzones.js membership test, at P)
    const zs = ctx.zones && ctx.zones.zones;
    if (zs) {
      for (const zn of zs) {
        const baseY = zn.baseY != null ? zn.baseY : zn.y;
        const d = Math.hypot(P.x - zn.cx, P.z - zn.cz);
        if (flat ? d <= zn.r + pickR * 0.5 : d <= zn.r && P.y > baseY - zn.r && P.y < baseY + zn.r + 8) {
          hits.push({ kind: 'station', name: zn.name || '', x: zn.cx, y: zn.y, z: zn.cz, data: { zone: zn } });
          break;
        }
      }
    }
    // lift line
    const lines = ctx.rides ? ctx.rides.lines : null;
    const liftR = flat ? Math.max(LIFT_R, Math.min(pickR * 0.6, 60)) : LIFT_R;
    if (lines && lines.length) {
      let best = null;
      for (const l of lines) {
        const pts = l.path && l.path.points;
        if (!pts || pts.length < 2) continue;
        const n = nearestOnLine(pts, P.x, P.z);
        if (n && n.d <= liftR && (!best || n.d < best.n.d)) best = { l, n };
      }
      if (best) {
        hits.push({ kind: 'lift', name: best.l.path.name || '', x: best.n.x, y: best.n.y, z: best.n.z,
          data: { line: best.l } });
      }
    }
    // water: a lake polygon, else a named river reach
    const w = ctx.water;
    const lvl = w && w.surfaceAt ? w.surfaceAt(P.x, P.z) : null;
    if (lvl !== null && lvl !== undefined) {
      // R12: what the provider's facts know of the lake (water.json `facts`)
      const lk = w.lakeAt ? w.lakeAt(P.x, P.z) : null;
      hits.push({ kind: 'water', name: (w.nameAt && w.nameAt(P.x, P.z)) || '', x: P.x, y: lvl, z: P.z,
        data: { lake: true, level: lvl, facts: lk ? lk.facts : null, temp: w.tempOfLake ? w.tempOfLake(lk) : null } });
    } else if (ctx.rivers && ctx.rivers.nameAt) {
      const rn = ctx.rivers.nameAt(P.x, P.z);
      // R12: the river's gauge nearest the aim (water.json `gauges`)
      const gauge = rn && w && w.gaugeOn ? w.gaugeOn(rn, P.x, P.z) : null;
      const temp = rn && w && w.tempOnRiver ? w.tempOnRiver(rn, P.x, P.z) : null;   // WATER-T
      if (rn) hits.push({ kind: 'water', name: rn, x: P.x, y: P.y, z: P.z, data: { river: true, gauge, temp } });
    }
  }

  // gazetteer: entries within r of P (r shrinks with zoom), plus — for a far
  // or sky aim — the optic label nearest the reticle (what the eye aimed at)
  const gz = [];
  const seen = new Set();
  const addPoi = (p, aimed) => {
    const key = p.kind + '|' + p.name;
    if (!p.name || seen.has(key)) return;
    // a station POI duplicates the station zone resolved above
    if (p.kind === 'station' && hits.some((h) => h.kind === 'station' && h.name === p.name)) return;
    if ((p.kind === 'lake' || p.kind === 'river') && hits.some((h) => h.kind === 'water' && h.name === p.name)) return;
    seen.add(key);
    const kind = gazKind(p.kind);
    const y = p.ele ?? ground(p.x, p.z) ?? P.y;
    gz.push({ kind, name: p.name, x: p.x, y, z: p.z, aimed,
      data: { poi: { name: p.name, kind: p.kind, ele: p.ele, prom: p.prom } } });
  };
  const dist = opts.dist ?? Infinity;
  if (opts.labels && (sky || dist > FAR_M)) {
    let best = null, bd = LABEL_PX;
    for (const f of opts.labels) {
      if (typeof f.x !== 'number') continue;
      const d = Math.hypot(f.sx - 512, f.sy - 320);
      if (d <= bd) { bd = d; best = f; }
    }
    if (best) addPoi(best, true);
  }
  if (!sky && ctx.gazetteer && ctx.gazetteer.within) {
    const r = flat ? Math.max(10, Math.min(400, pickR)) : Math.max(20, Math.min(150, 150 / zoom));
    for (const p of ctx.gazetteer.within(P.x, P.z, r).slice(0, 3)) addPoi(p, false);
  }
  // an aimed-at far summit leads (the eye chose it); else gazetteer after water
  const aimed = gz.filter((g) => g.aimed);
  hits.unshift(...aimed);
  hits.push(...gz.filter((g) => !g.aimed));
  // what the ray hit first leads: furniture or an object, the nearer
  const rayLead = [];
  if (fEnt && fh.how === 'ray') rayLead.push({ t: fh.t, e: fEnt });
  if (oEnt) rayLead.push({ t: orh.t, e: oEnt });
  rayLead.sort((a, b) => b.t - a.t);
  for (const l of rayLead) hits.unshift(l.e);

  // ground: always (sky = nothing along the ray)
  hits.push({ kind: 'ground', name: '', x: P.x, y: P.y, z: P.z, data: { sky } });
  return hits;
}

// ---------------------------------------------------------------- facets
// a route entity (the route card): route r (ctx.routes[ri]), stage si
function routeEnt(ctx, ri, x, z, si = null) {
  const r = ctx.routes && ctx.routes[ri];
  if (!r) return null;
  if (si === null || !r.stages[si]) {
    const n = nearestPoly(r.stages, x, z, Infinity);
    si = n ? r.stages.indexOf(n.item) : 0;
  }
  const y = ctx.rangeGround ? ctx.rangeGround(x, z) : null;
  return { kind: 'route', name: r.name || (r.ref ? t('player.insp.title.routeRef', { ref: r.ref }) : ''), x, y, z, data: { route: r, ri, si } };
}

// the path card (MAP-D): mark + official class, grade, MTB, width, surface,
// visibility, incline, the section / whole path + walking time both ways,
// the routes it is part of (tap → the route card). A TLM-only segment
// (pathinfo extra, R5): the official class, width, surface, length.
// "<there> one way · <back> back"
const walkBoth = (len, asc, desc) => t('player.insp.trail.walkBoth',
  { there: fmtHours(walkTimeH(len, asc, desc)), back: fmtHours(walkTimeH(len, desc, asc)) });
function trailRows(tr, env) {
  const rows = [];
  const sw = MARK[tr.mark] ? MARK[tr.mark].bands : [];
  if (tr.extra) {
    rows.push({ k: t('player.insp.row.official'), v: t('player.insp.trail.extraOfficial', { mark: MARK[tr.mark].label }), sw });
    if (tr.oa && OA_LABEL[tr.oa]) rows.push({ k: t('player.insp.row.width'), v: OA_LABEL[tr.oa] });
    if (tr.bel) rows.push({ k: t('player.insp.row.surface'), v: BEL_LABEL[tr.bel] || String(tr.bel) });
    rows.push({ k: t('player.insp.row.thisSection'), v: fmtRange(tr.len_m) });
    rows.push({ k: t('player.insp.row.inGame'), v: t('player.insp.trail.mapOnly'), dim: true });
    return rows;
  }
  const g = gradeText(tr);
  if (g) rows.push({ k: t('player.insp.row.grade'), v: g, sw });
  else rows.push({ k: t('player.insp.row.grade'), v: t('player.insp.trail.noGrade'), dim: true });
  if (tr.off && OFFICIAL_MARK[tr.off]) {
    const om = OFFICIAL_MARK[tr.off];
    rows.push({ k: t('player.insp.row.officialClass'), v: t('player.insp.trail.setsMark', { mark: MARK[om].label }) });
  }
  const tt = tr.tt || {};
  if (tt.vf) rows.push({ k: t('player.insp.row.viaFerrata'), v: /^\d+$/.test(String(tt.vf)) ? t('player.insp.trail.huesler', { n: tt.vf }) : String(tt.vf) });
  const mc = mtbClass(tt.mtb);
  if (mc) rows.push({ k: t('player.insp.row.mtb'), v: `${mc.label} · ${mc.word}`, mtb: mc.colour });
  else if (tt.mtb !== undefined && tt.mtb !== '') rows.push({ k: t('player.insp.row.mtb'), v: String(tt.mtb) });
  if (tr.oa && OA_LABEL[tr.oa]) rows.push({ k: t('player.insp.row.width'), v: OA_LABEL[tr.oa] });
  const surf = [tt.surface ? String(tt.surface).replace(/_/g, ' ') : '', tr.bel ? (BEL_LABEL[tr.bel] || tr.bel) + (tt.surface ? ' (TLM)' : '') : '']
    .filter(Boolean).join(' · ');
  if (surf) rows.push({ k: t('player.insp.row.surface'), v: surf });
  if (tt.tracktype) rows.push({ k: t('player.insp.row.trackType'), v: String(tt.tracktype) });
  if (tt.vis) rows.push({ k: t('player.insp.row.visibility'), v: VIS_LABEL[tt.vis] || String(tt.vis) });
  if (tt.incline) {
    const s = String(tt.incline);
    rows.push({ k: t('player.insp.row.incline'), v: s === 'up' ? t('player.insp.trail.uphill') : s === 'down' ? t('player.insp.trail.downhill') : s });
  }
  if (tr.fahr) rows.push({ k: t('player.insp.row.vehicles'), v: String(tr.fahr).replace(/_/g, ' ') });
  rows.push({ k: t('player.insp.row.thisSection'), v: `${fmtRange(tr.len_m)} · ↑ ${fmtInt(tr.asc_m)} m ↓ ${fmtInt(tr.desc_m)} m` });
  // the whole chain (runs sharing `chain`): totals + walking time both ways
  const all = env.ctx.roadnet && env.ctx.roadnet.trails;
  let len = tr.len_m, asc = tr.asc_m, desc = tr.desc_m, n = 1;
  if (all && tr.chain !== undefined && tr.chain !== null) {
    len = 0; asc = 0; desc = 0; n = 0;
    for (const r of all) if (r.chain === tr.chain) { len += r.len_m; asc += r.asc_m; desc += r.desc_m; n++; }
  }
  if (n > 1) rows.push({ k: t('player.insp.row.wholePath'), v: `${fmtRange(len)} · ↑ ${fmtInt(asc)} m ↓ ${fmtInt(desc)} m` });
  rows.push({ k: t('player.insp.row.walkingTime'), v: walkBoth(len, asc, desc) });
  // the routes this way is part of (meta.routes' reverse index) → route cards
  const R = env.ctx.routes, e = env.ent;
  if (R && Array.isArray(tr.routes) && tr.routes.length && e) {
    rows.push({ h: t('player.insp.h.onRoutes') });
    for (const ri of tr.routes.slice(0, 8)) {
      const r = R[ri];
      if (!r) continue;
      const ent = routeEnt(env.ctx, ri, e.x, e.z);
      if (!ent) continue;
      const fam = routeFamily(r);
      const net = NETWORK_LABEL[r.network] || '';
      rows.push({ k: `${ROUTE_STYLE[fam].icon} ${r.ref ? r.ref + ' · ' : ''}${r.name || routeName(r)}`,
        v: net || ROUTE_WORD[r.route] || t('player.insp.route.word'), tap: ent });
    }
  }
  return rows;
}

// the route card (MAP-D): plate + name, network, from → via → to, the
// official figures, MTB scale, loop, operator, "inside this map" + walking
// time, the stage, 🔗 website
function routeRows(e, env) {
  const r = e.data.route, rows = [];
  const plate = routePlate(r);
  const name = routeName(r);
  rows.push({ k: t('player.insp.row.route'), v: r.name || name, plate: r.ref ? { text: String(r.ref).slice(0, 10), col: plate } : null });
  const net = NETWORK_LABEL[r.network];
  rows.push({ k: t('player.insp.row.network'), v: `${net ? net + ' ' : ''}${name}${r.network ? ` (${r.network})` : ''}` });
  const path = [r.from, ...(Array.isArray(r.via) ? r.via : r.via ? [r.via] : []), r.to].filter(Boolean);
  if (path.length >= 2) rows.push({ k: t('player.insp.row.fromTo'), v: path.join(' → ') });
  const off = [];
  if (Number.isFinite(r.distance_km)) off.push(`${r.distance_km} km`);
  if (Number.isFinite(r.ascent_m)) off.push(`↑ ${fmtInt(r.ascent_m)} m`);
  if (Number.isFinite(r.descent_m)) off.push(`↓ ${fmtInt(r.descent_m)} m`);
  if (r.duration) off.push(String(r.duration));
  if (off.length) rows.push({ k: t('player.insp.row.official'), v: off.join(' · ') });
  const mc = mtbClass(r.mtb_scale);
  if (mc) rows.push({ k: t('player.insp.row.mtb'), v: `${mc.label} · ${mc.word}`, mtb: mc.colour });
  if (r.roundtrip) rows.push({ k: t('player.insp.row.loop'), v: t('player.insp.route.loopYes') });
  if (r.operator) rows.push({ k: t('player.insp.row.operator'), v: String(r.operator) });
  const L = safeLink(r.website);
  if (L) rows.push({ k: t('player.insp.row.website'), v: L.host, href: L.href });
  const inn = r.in;
  if (inn && Number.isFinite(inn.len_m)) {
    rows.push({ h: t('player.insp.h.insideMap') });
    const a = inn.asc_m || 0, d = inn.desc_m || 0;
    rows.push({ k: t('player.insp.row.length'), v: `${fmtRange(inn.len_m)} · ↑ ${fmtInt(a)} m ↓ ${fmtInt(d)} m` });
    rows.push({ k: t('player.insp.row.walkingTime'), v: walkBoth(inn.len_m, a, d) });
  }
  const st = r.stages[e.data.si];
  if (st && r.stages.length > 1) {
    const lm = Number.isFinite(st.len_m) ? st.len_m : st.len;
    rows.push({ k: t('player.insp.row.stage'), v: `${t('player.insp.route.nOf', { n: e.data.si + 1, total: r.stages.length })} · ${fmtRange(lm)}`
      + (Number.isFinite(st.asc_m) ? ` · ↑ ${fmtInt(st.asc_m)} m ↓ ${fmtInt(st.desc_m || 0)} m` : '') });
  }
  const g = env.ctx.beacons && env.ctx.beacons.guide;
  if (g && g.ri === e.data.ri) {
    rows.push({ k: t('player.insp.row.guide'), v: g.dir < 0
      ? t('player.insp.route.followingRev', { n: g.si + 1, ahead: GUIDE.ahead_m })
      : t('player.insp.route.following', { n: g.si + 1, ahead: GUIDE.ahead_m }) });
  }
  return rows;
}

// the route card's actions: 🧭 Guide me / stop, ⇄ reverse (while guiding),
// ⏮ ⏭ stages. Combat on hides the waypoint → no guide.
function routeActions(e, env) {
  const ctx = env.ctx, ui = env.ui, out = [];
  const r = e.data.route, n = r.stages.length;
  const combat = !!(ctx.combatOn && ctx.combatOn());
  const B = ctx.beacons;
  const g = B && B.guide;
  const mine = !!(g && g.ri === e.data.ri && g.si === e.data.si);
  const label = (r.ref ? r.ref + ' ' : '') + (r.name || '');
  const start = (dir) => {
    const st = r.stages[e.data.si];
    const p = ctx.player ? ctx.player.pos : { x: e.x, z: e.z };
    const d = dir || RouteGuide.dirFor(st.pts, p.x, p.z);
    const rg = new RouteGuide(st.pts, { dir: d, label: (label.trim() || t('player.insp.route.word')).slice(0, 30), ri: e.data.ri, si: e.data.si });
    B.startGuide(rg);
    B.guideTick(p.x, p.z, ctx.rangeGround);
    return d;
  };
  if (B) {
    if (mine) {
      out.push({ id: 'unguide', icon: '🧭', label: t('player.insp.act.stopGuide'), run() { B.clearWaypoint(); return t('player.insp.res.guideStopped'); } });
      out.push({ id: 'reverse', icon: '⇄', label: t('player.insp.act.reverse'), disabled: combat,
        run() { start(-g.dir); return t('player.insp.res.guideReversed'); } });
    } else {
      out.push({ id: 'guide', icon: '🧭', label: combat ? t('player.insp.act.guideCombat') : t('player.insp.act.guide'), disabled: combat,
        run() {
          start(0);
          return t('player.insp.res.guiding', { name: label.trim() || t('player.insp.res.theRoute'), ahead: GUIDE.ahead_m });
        } });
    }
  }
  if (n > 1) {
    const go = (k) => () => {
      e.data.si = (e.data.si + k + n) % n;
      if (ui) ui._mapSelect(true);
      return t('player.insp.res.stageOf', { n: e.data.si + 1, total: n });
    };
    out.push({ id: 'prev', icon: '⏮', label: t('player.insp.act.prevStage'), run: go(-1) });
    out.push({ id: 'next', icon: '⏭', label: t('player.insp.act.nextStage'), run: go(1) });
  }
  return out;
}

// the amenity card (MAP-D): kind, name, height, tags; DETAIL-1 street
// items (amenities.json `street`, t.src): a bus stop's official number,
// shelter, bench (amenity bench reused → that bench), the source
const SRC_LABEL = { osm: 'OpenStreetMap', tlm: 'swissTLM3D', 'osm+tlm': 'OpenStreetMap + swissTLM3D' };
// a building's OSM tag keys shown as rows (the key, capitalised)
const OSM_KEY_LABEL = lazyLabels({ amenity: () => t('player.insp.kind.amenity'), tourism: () => t('player.insp.row.tourism'),
  historic: () => t('player.insp.row.historic'), shop: () => t('player.insp.row.shop'), office: () => t('player.insp.row.office') });
const yesNo = (v) => (v === true ? t('player.insp.val.yes') : v === false ? t('player.insp.val.no') : null);
// D1-BLD: the shops in a building (registry `shops`, OSM named shop nodes
// inside / <= 5 m outside; never its name) — name, kind, hours, website link
export function shopRows(rec) {
  const list = rec && Array.isArray(rec.shops) ? rec.shops : [];
  if (!list.length) return [];
  const rows = [{ h: list.length === 1 ? t('player.insp.row.shop') : t('player.insp.h.shops', { n: list.length }) }];
  for (const sh of list) {
    if (!sh || !sh.name) continue;
    const kind = sh.kind && sh.kind !== 'yes' ? String(sh.kind).replace(/_/g, ' ') : t('player.insp.val.shop');
    const L = sh.website ? safeLink(sh.website) : null;
    rows.push({ k: `🛍 ${sh.name}`, v: kind, ...(L ? { href: L.href } : {}) });
    if (sh.opening_hours) rows.push({ k: '', v: `🕑 ${sh.opening_hours}`, dim: true });
  }
  return rows;
}

export function amenityRows(e) {
  const p = e.data.am, rows = [], tg = p.t || {};
  rows.push({ k: t('player.insp.row.type'), v: amenityLabel(p) });
  if (p.name) rows.push({ k: t('player.insp.row.name'), v: String(p.name) });
  if (p.k === 'bus_stop') {
    if (Number.isInteger(tg.ref)) rows.push({ k: t('player.insp.row.stopNumber'), v: String(tg.ref) });
    for (const [k, v] of [[t('player.insp.row.shelter'), yesNo(tg.shelter)], [t('player.insp.row.bench'), yesNo(tg.bench)]]) if (v) rows.push({ k, v });
  }
  if (typeof tg.src === 'string' && SRC_LABEL[tg.src]) {
    rows.push({ k: t('player.insp.row.source'), v: SRC_LABEL[tg.src] + (p.k === 'barrier' ? ' (Durchfahrtssperre)' : ''), dim: true });
    return rows;
  }
  const ele = Number.isFinite(p.ele) ? p.ele : null;
  if (ele !== null) rows.push({ k: p.k === 'saddle' ? t('player.insp.row.passHeight') : t('player.insp.row.height'), v: `${fmtInt(ele)} m` });
  const tags = Object.keys(p.t || {}).filter((k) => p.t[k] && TAG_LABEL[k])
    .map((k) => (k === 'historic' && typeof p.t[k] === 'string' ? String(p.t[k]).replace(/_/g, ' ') : TAG_LABEL[k]));
  if (tags.length) rows.push({ k: t('player.insp.row.details'), v: tags.join(' · ') });
  return rows;
}

// a graded trail run with an end within r of (x, z) → that trail, else null
function trailEndNear(ctx, x, z, r = 150) {
  const all = ctx.roadnet && ctx.roadnet.trails;
  if (!all) return null;
  for (const t of all) {
    if (t.mark === 'none' || !t.pts || t.pts.length < 4) continue;
    const bb = t.bbox;
    if (bb && (x < bb[0] - r || x > bb[2] + r || z < bb[1] - r || z > bb[3] + r)) continue;
    const p = t.pts, L = p.length;
    if (Math.hypot(p[0] - x, p[1] - z) <= r || Math.hypot(p[L - 2] - x, p[L - 1] - z) <= r) return t;
  }
  return null;
}

function slopeAspect(ctx, x, z) {
  let n = null, approx = false;
  if (ctx.hf && ctx.hf.normal) n = ctx.hf.normal(x, z, new THREE.Vector3());
  if (!n && ctx.rangeGround) {
    const e = 15, g = ctx.rangeGround;
    const a = g(x + e, z), b = g(x - e, z), c = g(x, z + e), d = g(x, z - e);
    if (a !== null && b !== null && c !== null && d !== null) {
      n = new THREE.Vector3(-(a - b) / (2 * e), 1, -(c - d) / (2 * e)).normalize();
      approx = true;
    }
  }
  if (!n) return null;
  const slope = Math.acos(Math.min(1, n.y)) * 180 / Math.PI;
  const asp = bearingDeg(0, 0, n.x, n.z);    // the downhill side faces this way
  return { slope, asp, approx };
}

// a drawn way names its own surface (ways2.js wayLabelAt: the tag, else its
// look kind); the AI class map only off the ways
function surfaceLabel(ctx, x, z) {
  const w = ctx.roadnet && ctx.roadnet.wayLabelAt ? ctx.roadnet.wayLabelAt(x, z) : null;
  if (w) return w;
  const s = ctx.surface;
  if (!s) return null;
  const c = s.at ? s.at(x, z) : null;
  if (c && c.label) return c.label;
  // coarse class grid (the whole map, ~coarse stride) when the 3×3 tiles miss
  try {
    const m = s.meta, C = m && m.coarse;
    if (!C || !m.classes) return null;
    if (!s._inspCoarse) s._inspCoarse = s.field(C.stride);
    const f = s._inspCoarse;
    const cx = Math.floor(x / f.cell), cz = Math.floor(z / f.cell);
    if (cx < 0 || cz < 0 || cx >= f.w || cz >= f.h) return null;
    const id = f.data[cz * f.w + cx];
    const cls = m.classes.find((k) => k.id === id);
    return cls && cls.label ? t('player.insp.val.coarse', { label: cls.label }) : null;
  } catch { return null; }
}

export const INSPECT_FACETS = [
  {
    id: 'building',
    match: (e) => e.kind === 'building',
    render(e, env) {
      const rows = [];
      const rec = e.data.rec, g = (rec && rec.gwr) || {}, o = (rec && rec.osm) || {};
      let addr = null;
      if (g.addr) addr = `${g.addr}${g.plz || g.muni ? `, ${[g.plz, g.muni].filter(Boolean).join(' ')}` : ''}`;
      else if (o['addr:street']) {
        addr = `${o['addr:street']}${o['addr:housenumber'] ? ' ' + o['addr:housenumber'] : ''}`;
        const city = [o['addr:postcode'], o['addr:city']].filter(Boolean).join(' ');
        if (city) addr += `, ${city}`;
      }
      if (addr) rows.push({ k: t('player.insp.row.address'), v: addr });
      else rows.push({ k: t('player.insp.row.address'), v: env.ui && env.ui._regState === 'loading' ? t('player.insp.val.regLoading') : t('player.insp.val.noAddress'), dim: true });
      if (g.label && g.label !== e.name) rows.push({ k: t('player.insp.row.registerName'), v: g.label });
      if (g.cls && GWR_CLS[g.cls]) rows.push({ k: t('player.insp.row.buildingClass'), v: GWR_CLS[g.cls] });
      if (g.cat && GWR_CAT[g.cat]) rows.push({ k: t('player.insp.row.category'), v: GWR_CAT[g.cat] });
      if (g.year) rows.push({ k: t('player.insp.row.built'), v: String(g.year) });
      else if (g.era) {
        // 'pre1919' → "before 1919"
        const era = String(g.era), i = era.indexOf('pre');
        rows.push({ k: t('player.insp.row.built'), v: i < 0 ? era : era.slice(0, i) + t('player.insp.val.before', { y: era.slice(i + 3) }) });
      }
      if (g.storeys) rows.push({ k: t('player.insp.row.storeys'), v: String(g.storeys) });
      if (g.dwellings) rows.push({ k: t('player.insp.row.dwellings'), v: String(g.dwellings) });
      if (g.area) rows.push({ k: t('player.insp.row.footprint'), v: `${fmtInt(g.area)} m²` });
      if (g.protected) rows.push({ k: t('player.insp.row.protected'), v: g.protected === true ? t('player.insp.val.yes') : String(g.protected) });
      for (const key of ['amenity', 'tourism', 'historic', 'shop', 'office']) {
        if (o[key] && o[key] !== 'yes') rows.push({ k: OSM_KEY_LABEL[key], v: String(o[key]).replace(/_/g, ' ') });
      }
      if (o.wikidata) rows.push({ k: 'Wikidata', v: String(o.wikidata) });
      rows.push(...shopRows(rec));
      const h = e.data.top != null ? e.data.top - e.y : 0;
      if (h > 0) rows.push({ k: t('player.insp.row.height'), v: t('player.insp.val.eavesRidge', { h: Math.round(h) }) });
      return rows;
    },
    had: (e, rows) => rows.some((r) => r.k === t('player.insp.row.address') && !r.dim),
  },
  {
    id: 'trail',
    match: (e) => e.kind === 'trail',
    render: (e, env) => trailRows(e.data.trail, env),
    had: (e) => e.data.trail.mark !== 'none' || !!e.data.trail.tt,
  },
  {
    id: 'route',
    match: (e) => e.kind === 'route',
    render: (e, env) => routeRows(e, env),
    had: () => true,
    actions: (e, env) => routeActions(e, env),
  },
  {
    id: 'amenity',
    match: (e) => e.kind === 'amenity',
    render: (e) => amenityRows(e),
    had: () => true,
    // 🧭 Guide me: the plain waypoint (the compass strip points there)
    actions(e, env) {
      const B = env.ctx.beacons;
      if (!B) return [];
      const combat = !!(env.ctx.combatOn && env.ctx.combatOn());
      return [{ id: 'amguide', icon: '🧭', label: combat ? t('player.insp.act.guideCombat') : t('player.insp.act.guide'), disabled: combat,
        run() {
          const y = env.ctx.rangeGround ? env.ctx.rangeGround(e.x, e.z) : null;
          B.setWaypoint(e.x, e.z, titleOf(e), y ?? e.y ?? 0);
          return t('player.insp.res.pointThere');
        } }];
    },
  },
  {
    id: 'object',
    match: (e) => e.kind === 'object',
    render: (e) => objectRows(e, e.data.doc),
    had: () => true,
  },
  {
    id: 'peak',
    match: (e) => e.kind === 'peak',
    render(e, env) {
      const p = e.data.poi, rows = [];
      rows.push({ k: t('player.insp.row.type'), v: POI_LABEL[p.kind] || p.kind });
      const eleK = p.kind === 'pass' ? t('player.insp.row.passHeight') : p.kind === 'ridge' ? t('player.insp.row.ridgeCrest') : t('player.insp.kind.peak');
      if (p.ele) rows.push({ k: eleK, v: `${fmtInt(p.ele)} m` });
      if (p.prom) rows.push({ k: t('player.insp.row.prominence'), v: `${fmtInt(p.prom)} m` });
      const te = trailEndNear(env.ctx, e.x, e.z, 150);
      if (te) rows.push({ k: t('player.insp.row.trail'), v: t('player.insp.val.pathEnds', { grade: gradeText(te) }), sw: MARK[te.mark].bands });
      return rows;
    },
    had: (e) => !!(e.data.poi.ele || e.data.poi.prom),
  },
  {
    id: 'station',
    match: (e) => e.kind === 'station',
    render(e, env) {
      const rows = [];
      const zn = e.data.zone;
      if (e.data.ride) {
        const zr = env.ctx.zones && env.ctx.zones._rows;
        if (zr) for (const r of zr) if (r.dim) rows.push({ k: '', v: r.text, dim: true });
        return rows;
      }
      if (zn && zn.actions) {
        const seen = new Set();
        for (const a of zn.actions) {
          const s = `→ ${a.dest || '?'} (${((l) => (lang() === 'en' ? l.toLowerCase() : l))(LIFT_LABEL[a.kind] || a.kind || t('player.insp.val.line'))})`;
          if (!seen.has(s)) { seen.add(s); rows.push({ k: seen.size === 1 ? t('player.insp.row.lines') : '', v: s }); }
        }
      } else if (e.data.poi) rows.push({ k: t('player.insp.row.type'), v: POI_LABEL.station });
      return rows;
    },
    // the sign's enabled rows (board / sit on / exit) — only for the zone the
    // player stands in (or the ride they are on): the rows are that sign's
    actions(e, env) {
      const zs = env.ctx.zones;
      if (!zs || !ownsStationRows(zs, e)) return [];
      return stationRows(zs).map((r, i) => ({
        id: `station${i}`, icon: '◎', label: String(r.text).replace(/^◎\s*/, '').replace(/\s*·\s*[^·]*\d+\s*s$/, ''),
        run() {
          if (r.onShoot() === false) return t('player.insp.res.notNow');
          if (env.ctx.sfx && env.ctx.sfx.ok) env.ctx.sfx.ok();
          if (env.ui) env.ui.close('station');
          return null;
        },
      }));
    },
  },
  {
    id: 'lift',
    match: (e) => e.kind === 'lift',
    render(e) {
      const l = e.data.line, p = l.path, pts = p.points, rows = [];
      rows.push({ k: t('player.insp.row.type'), v: LIFT_LABEL[l.kind || 'funicular'] || String(l.kind) });
      const ends = String(p.name || '').split(/\s+[-–—]\s+/);
      if (ends.length === 2) rows.push({ k: t('player.insp.row.fromTo'), v: `${ends[0]} → ${ends[1]}` });
      let len = p.length;
      if (!len) { len = 0; for (let i = 1; i < pts.length; i++) len += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1], pts[i][2] - pts[i - 1][2]); }
      rows.push({ k: t('player.insp.row.lineLength'), v: fmtRange(len) });
      const a = pts[0][1], b = pts[pts.length - 1][1];
      rows.push({ k: t('player.insp.row.vertical'), v: `${fmtInt(Math.abs(b - a))} m (${fmtInt(Math.min(a, b))} → ${fmtInt(Math.max(a, b))} m)` });
      return rows;
    },
  },
  {
    id: 'water',
    match: (e) => e.kind === 'water',
    render(e) {
      const rows = [];
      const d = e.data;
      const f = d.facts || {};
      // R12: every fact row only when the provider knows it (absent = unknown)
      const kind = d.lake || (d.poi && d.poi.kind === 'lake')
        ? (f.reservoir ? (f.natural ? t('player.insp.water.reservoirNatural') : t('player.insp.water.reservoir'))
          : f.natural ? t('player.insp.water.naturalLake') : POI_LABEL.lake)
        : POI_LABEL.river;
      rows.push({ k: t('player.insp.row.type'), v: kind });
      if (f.official_name && f.official_name !== e.name) rows.push({ k: t('player.insp.row.officialName'), v: f.official_name });
      if (d.level != null) rows.push({ k: t('player.insp.row.waterLevel'), v: `${fmtInt(d.level)} m` });
      // WATER-T: the live temperature (water.js fmtTempRow); unknown = no row
      if (d.temp) rows.push({ k: t('player.insp.row.waterTemp'), v: fmtTempRow(d.temp), ...(d.temp.stale ? { dim: true } : {}) });
      const by = FACTS_BY[String(f.src || '').split(':')[0]] || '';
      if (f.max_depth_m > 0) rows.push({ k: t('player.insp.row.maxDepth'), v: `${fmtDepth(f.max_depth_m)} m` + (by ? ` (${by})` : '') });
      if (f.area_km2 > 0) rows.push({ k: t('player.insp.row.area'), v: `${f.area_km2} km²` });
      if (f.shore_m > 0) rows.push({ k: t('player.insp.row.shore'), v: fmtRange(f.shore_m) });
      if (f.elevation_m > 0) rows.push({ k: t('player.insp.row.elevation'), v: `${fmtInt(f.elevation_m)} m` });
      if (f.volume_mio_m3 > 0) rows.push({ k: t('player.insp.row.volume'), v: t('player.insp.val.mioM3', { v: f.volume_mio_m3 }) });
      if (f.usable_mio_m3 > 0) rows.push({ k: t('player.insp.row.usableVolume'), v: t('player.insp.val.mioM3', { v: f.usable_mio_m3 }) });
      if (typeof f.regulated === 'boolean') rows.push({ k: t('player.insp.row.regulated'), v: f.regulated ? t('player.insp.val.yes') : t('player.insp.val.no') });
      // the gauge: a tappable link to its public live page (no live data here)
      const g = f.gauge || d.gauge;
      const L = g ? safeLink(g.url) : null;
      if (g) rows.push({ k: t('player.insp.row.gauge'), v: g.name || g.nr, ...(L ? { href: L.href } : {}) });
      if (!e.name) rows.push({ k: t('player.insp.row.name'), v: t('player.insp.val.unnamed'), dim: true });
      return rows;
    },
    had: (e) => !!e.name,
  },
  {
    id: 'poi',
    match: (e) => e.kind === 'poi',
    render(e) {
      const p = e.data.poi, rows = [{ k: t('player.insp.row.type'), v: POI_LABEL[p.kind] || String(p.kind).replace(/_/g, ' ') }];
      if (p.ele) rows.push({ k: t('player.insp.row.elevation'), v: `${fmtInt(p.ele)} m` });
      return rows;
    },
  },
  {
    id: 'ground',
    match: (e) => e.kind === 'ground',
    render(e, env) {
      if (e.data.sky) return [{ k: t('player.insp.row.range'), v: t('player.insp.val.openSkyLine'), dim: true }];
      const rows = [];
      // AIM-SKY: the ray's true ground (L0) is still loading — never "sky"
      if (e.data.pending) rows.push({ k: t('player.insp.row.range'), v: t('player.insp.val.measuring'), dim: true });
      if (e.data.undecided) return rows;
      const s = surfaceLabel(env.ctx, e.x, e.z);
      rows.push(s ? { k: t('player.insp.row.surface'), v: s } : { k: t('player.insp.row.surface'), v: t('player.insp.val.noClassMap'), dim: true });
      const sa = slopeAspect(env.ctx, e.x, e.z);
      if (sa) {
        rows.push({ k: t('player.insp.row.slope'), v: `${sa.approx ? '≈ ' : ''}${Math.round(sa.slope)}°` });
        if (sa.slope >= 2) rows.push({ k: t('player.insp.row.faces'), v: `${Math.round(sa.asp)}° ${compass8(sa.asp)}` });
      }
      return rows;
    },
    had: (e, rows) => rows.some((r) => r.k === t('player.insp.row.surface') && !r.dim),
  },
  {
    // coordinates for every non-sky target: LV95 + WGS84 + game xz (not
    // for an undecided ray: no place yet)
    id: 'position',
    match: (e) => !(e.kind === 'ground' && (e.data.sky || e.data.undecided)),
    render(e, env) {
      const rows = [{ h: t('player.insp.h.position') }];
      const lv = gameToLv95(e.x, e.z, env.ctx.meta);
      if (lv) {
        const w = lv95ToWgs84(lv.E, lv.N);
        rows.push({ k: 'LV95', v: `${fmtInt(lv.E)} / ${fmtInt(lv.N)}` });
        rows.push({ k: 'WGS84', v: `${w.lat.toFixed(5)}° N, ${w.lon.toFixed(5)}° E` });
      }
      rows.push({ k: t('player.insp.row.game'), v: `x ${Math.round(e.x)} · z ${Math.round(e.z)}`, dim: true });
      return rows;
    },
  },
  {
    // what's near the TARGET (not the player): gazetteer within 500 m, 8
    id: 'near',
    match: (e) => !(e.kind === 'ground' && (e.data.sky || e.data.undecided)),
    render(e, env) {
      const g = env.ctx.gazetteer;
      if (!g || !g.within) return [];
      const list = g.within(e.x, e.z, 500)
        .filter((p) => p.name && !(p.name === e.name && gazKind(p.kind) === e.kind)).slice(0, 8);
      if (!list.length) return [];
      const rows = [{ h: t('player.insp.h.near') }];
      for (const p of list) {
        const b = bearingDeg(e.x, e.z, p.x, p.z);
        const kind = gazKind(p.kind);
        rows.push({ k: `${LABEL_ICON[p.kind] || KIND_ICON[kind] || '📍'} ${p.name}`,
          v: `${fmtRange(p.dist)} ${compass8(b)}`, tap: { kind, name: p.name, x: p.x, y: p.ele ?? null, z: p.z,
            data: { poi: { name: p.name, kind: p.kind, ele: p.ele, prom: p.prom } } } });
      }
      return rows;
    },
  },
  {
    // the base actions, every target
    id: 'core',
    match: () => true,
    render: () => [],
    actions: (e, env) => coreActions(e, env),
  },
];

function iconOf(e) {
  if (e.kind === 'building') {
    const rec = e.data.rec, o = (rec && rec.osm) || {}, g = (rec && rec.gwr) || {};
    return LABEL_ICON[o.tourism] || LABEL_ICON[o.amenity] || (g.cls === 1272 ? '⛪' : g.cls === 1211 ? '🏨' : '🏠');
  }
  if (e.kind === 'lift') return e.data.line.kind ? '🚡' : '🚞';
  if (e.kind === 'route') return ROUTE_STYLE[routeFamily(e.data.route)].icon;
  if (e.kind === 'amenity') return (AMENITY[e.data.am.k] || [])[1] || '📍';
  if (e.kind === 'object') return OBJ_ICON[e.data.o.a] || '🧱';
  if (e.kind === 'ground' && e.data.sky) return '☁';
  if (e.data && e.data.poi) return LABEL_ICON[e.data.poi.kind] || KIND_ICON[e.kind] || '📍';
  return KIND_ICON[e.kind] || '📍';
}

function titleOf(e) {
  if (e.name) return e.name;
  if (e.kind === 'trail') return e.data && e.data.trail && e.data.trail.extra ? t('player.insp.title.officialPath') : t('player.insp.title.unnamedPath');
  if (e.kind === 'route') return e.data.route.ref ? t('player.insp.title.routeRef', { ref: e.data.route.ref }) : t('player.insp.kind.route');
  if (e.kind === 'amenity') return amenityLabel(e.data.am);
  if (e.kind === 'object') return objLabel(e.data.o);
  if (e.kind === 'ground') return e.data.sky ? t('player.insp.title.openSky') : t('player.insp.kind.ground');
  return KIND_LABEL[e.kind] || t('player.insp.title.target');
}

function provenanceOf(e, env) {
  const s0 = env.target.src;
  const src = s0 === 'depth' ? t('player.insp.src.optic') : s0 === 'march' ? t('player.insp.src.ray')
    : s0 === 'map' ? t('player.insp.src.mapTap') : t('player.insp.src.noRange');
  const OSM = t('player.insp.src.osm'), TLM = 'swissTLM3D (swisstopo)', DEM = t('player.insp.src.dem');
  const att = {
    building: e.data.rec
      ? `GWR (BFS) · ${OSM}${e.data.how === 'registry' ? ` · ${t('player.insp.src.nearestReg')}` : ''}`
      : 'swissBUILDINGS3D (swisstopo)',
    trail: e.data && e.data.trail && e.data.trail.extra ? TLM
      : `${OSM} · ${t('player.insp.src.sacScale')}${e.data && e.data.trail && e.data.trail.off ? ` · ${TLM}` : ''}`,
    route: `${OSM} · ${t('player.insp.src.routeRel')}`,
    amenity: e.data && e.data.am && e.data.am.t && e.data.am.t.src === 'tlm' ? TLM
      : `${OSM}${e.data && e.data.am && e.data.am.t && e.data.am.t.src === 'osm+tlm' ? ` · ${TLM}` : ''}`,
    peak: `${OSM} · ${DEM}`,
    poi: OSM,
    station: OSM,
    lift: OSM,
    water: `${OSM} · ${TLM}`
      + (FACTS_ATT[String(((e.data && e.data.facts) || {}).src || '').split(':')[0]] || '')
      + (e.data && (e.data.gauge || (e.data.facts && e.data.facts.gauge)) ? ` · ${t('player.insp.src.bafuGauges')}` : ''),
    ground: `${DEM} · ${t('player.insp.src.classMap')}`,
    object: e.data && e.data.o && objIsTlm(e.data.o) ? TLM : OSM,
  }[e.kind];
  return `${att} · ${src}`;
}

// the base actions: 📍 beacon, 🗺 map (👁 look on a Map card), ⤳ beam
// (the gradings are always on — no toggle). Combat on (a Map card): no beacon, no beam. Before entry (the
// Map before entry, ctx.preEntry()) ⤳ Beam IS the entry — shown in combat
// too (the server accepts the first place) — and there is no 👁 Look / 🗺 Map
// (no player to turn, the Map is already the page).
function coreActions(e, env) {
  const ctx = env.ctx, ui = env.ui, out = [];
  // an undecided ray (AIM-SKY, terrain loading) has no place to act on yet
  const sky = e.kind === 'ground' && (e.data.sky || e.data.undecided);
  const label = titleOf(e);
  const fromMap = !!(ui && ui._origin === 'map');
  const combat = !!(ctx.combatOn && ctx.combatOn());
  const pre = !!(ctx.preEntry && ctx.preEntry());
  if (!sky && ctx.beacons && !combat) {
    const on = ctx.beacons.isWaypoint && ctx.beacons.isWaypoint(e.x, e.z);
    out.push({ id: 'beacon', icon: '📍', label: on ? t('player.insp.act.beaconOff') : t('player.insp.act.beacon'),
      run() {
        if (ctx.beacons.isWaypoint(e.x, e.z)) { ctx.beacons.clearWaypoint(); return t('player.insp.res.beaconCleared'); }
        const y = ctx.rangeGround ? ctx.rangeGround(e.x, e.z) : null;
        ctx.beacons.setWaypoint(e.x, e.z, label, y ?? e.y ?? 0);
        return t('player.insp.res.beaconSet', { name: label });
      } });
  }
  if (pre) { /* before entry: no Look, no Map */ } else if (!sky && fromMap && ctx.lookAt) {
    // the Map's card: close the Map and face it (desktop eases the view; the
    // phone keeps its gyro view) — the pin marks it on every map + the HUD
    out.push({ id: 'look', icon: '👁', label: t('player.insp.act.look'),
      run() {
        const y = e.y ?? (ctx.rangeGround ? ctx.rangeGround(e.x, e.z) : null);
        ui.close('look');
        ctx.lookAt(e.x, y, e.z, label);
        return null;
      } });
  } else if (!sky && ctx.playerMap) {
    out.push({ id: 'map', icon: '🗺', label: t('player.insp.act.map'),
      run() {
        const opened = ctx.playerMap.focus(e.x, e.z, label);
        if (opened) {
          ui.close('map');
          return null;
        }
        return ctx.mode() === 'vr' ? t('player.insp.res.pinnedWrist') : t('player.insp.res.pinnedMap');
      } });
  }
  if (!sky && (!combat || pre)) {
    const bp = ui ? ui._beamPoint(e) : null;
    out.push({ id: 'beam', icon: '⤳', label: !bp ? t('player.insp.act.beamOutside') : pre ? t('player.insp.act.beamStart') : t('player.insp.act.beamThere'), disabled: !bp, bp,
      run() {
        if (!bp) return t('player.insp.res.refusedOutside');
        return ui._beam(bp);
      } });
  }
  return out;
}

// target + the index of the leading hit → the card model
export function buildCard(ctx, target, idx = 0, ui = null) {
  const e = target.hits[idx];
  const env = { ctx, target, ent: e, ui };
  // target.eye null = no player yet (a card on the Map before entry): nothing
  // is measured from the player — no distance, bearing or Δh, only the point
  const eye = target.eye;
  // an undecided ray's point is in the air: no elevation / Δh for it
  const ele = e.kind === 'peak' && e.data.poi.ele ? e.data.poi.ele : e.kind === 'ground' && e.data.undecided ? null : e.y;
  const sky = e.kind === 'ground' && e.data.sky;
  let head, sub;
  if (!eye) {
    head = { dist: null, brg: null, ele, dh: null };
    sub = [];
  } else {
    const dist = Math.hypot(e.x - eye.x, (e.y ?? eye.y) - eye.y, e.z - eye.z);
    const brg = bearingDeg(eye.x, eye.z, e.x, e.z);
    const feet = ctx.player ? ctx.player.pos.y : eye.y;
    head = sky ? { dist: Infinity, brg, ele: null, dh: null }
      : { dist, brg, ele, dh: Number.isFinite(ele) ? ele - feet : null };
    // a pending target (AIM-SKY) is provisional: '≤' the true ground lies at
    // or before this envelope hit (the envelope never stands above it), '≥'
    // no hit yet, the ground is at or beyond this undecided stretch
    const pend = !sky && target.bound && e.kind === 'ground' ? target.bound : '';
    sub = [sky ? '∞' : pend + fmtRange(dist), `${Math.round(brg)}° ${compass8(brg)}`];
  }
  if (Number.isFinite(head.ele)) sub.push(`${fmtInt(head.ele)} m`);
  if (Number.isFinite(head.dh)) sub.push(`Δh ${fmtSigned(head.dh)} m`);
  const rows = [], actions = [];
  let had = false, first = true;
  for (const f of INSPECT_FACETS) {
    if (!f.match(e)) continue;
    let r = [];
    try { r = f.render ? f.render(e, env) || [] : []; } catch (err) { console.error('[inspect facet]', f.id, err); }
    if (first && r.length) {
      had = f.had ? !!f.had(e, r) : r.some((x) => !x.dim && !x.h);
      first = false;
    }
    rows.push(...r);
    if (f.actions) {
      try { actions.push(...(f.actions(e, env) || [])); } catch (err) { console.error('[inspect actions]', f.id, err); }
    }
  }
  const chips = [];
  target.hits.forEach((h, i) => {
    if (i === idx || chips.length >= MAX_CHIPS) return;
    if (h.kind === 'ground' && h.data.sky) return;
    chips.push({ i, icon: iconOf(h), label: titleOf(h).slice(0, 26) });
  });
  return { title: titleOf(e), icon: iconOf(e), kind: e.kind, sub: sub.join(' · '), head,
    rows, chips, actions, provenance: provenanceOf(e, env), status: '', had, ent: e };
}

// ------------------------------------------------------- canvas painter
// The SAME card model on a 2D canvas (VR world plane). w = canvas width;
// h = canvas height to draw into, or 0 to measure only. → {height, boxes}
// boxes [{x, y, w, h, type: 'action'|'chip'|'near', i}] in canvas px.
export function paintCard(g, model, w, h = 0) {
  const P = 18, draw = h > 0, boxes = [];
  let y = P;
  const font = (px, wt = 400) => { g.font = `${wt} ${px}px system-ui, -apple-system, 'Segoe UI', sans-serif`; };
  const wrap = (text, maxW) => {
    const words = String(text).split(/\s+/), lines = [];
    let cur = '';
    for (const wd of words) {
      const tx = cur ? cur + ' ' + wd : wd;
      if (g.measureText(tx).width > maxW && cur) { lines.push(cur); cur = wd; } else cur = tx;
    }
    if (cur) lines.push(cur);
    return lines.length ? lines : [''];
  };
  if (draw) {
    g.clearRect(0, 0, w, h);
    g.fillStyle = 'rgba(9,15,25,0.94)';
    g.strokeStyle = 'rgba(34,211,238,0.6)'; g.lineWidth = 2;
    g.beginPath(); g.roundRect ? g.roundRect(1, 1, w - 2, h - 2, 16) : g.rect(1, 1, w - 2, h - 2);
    g.fill(); g.stroke();
    g.textBaseline = 'top';
  }
  // header
  font(26, 700);
  const tl = wrap(`${model.icon} ${model.title}`, w - 2 * P);
  for (const l of tl) { if (draw) { g.fillStyle = '#f1f5fb'; g.fillText(l, P, y); } y += 31; }
  font(16, 500);
  for (const l of wrap(model.sub, w - 2 * P)) { if (draw) { g.fillStyle = '#67e8f9'; g.fillText(l, P, y); } y += 21; }
  y += 6;
  // chips
  if (model.chips.length) {
    font(15, 600);
    let x = P;
    const also = t('player.insp.alsoHere');
    if (draw) { g.fillStyle = '#93a5c0'; g.fillText(also, x, y + 7); }
    x += g.measureText(also).width + 10;
    for (const c of model.chips) {
      const tx = `${c.icon} ${c.label}`, cw = g.measureText(tx).width + 20;
      if (x + cw > w - P) { x = P; y += 36; }
      if (draw) {
        g.fillStyle = 'rgba(103,232,249,0.12)'; g.strokeStyle = 'rgba(103,232,249,0.5)'; g.lineWidth = 1.5;
        g.beginPath(); g.roundRect ? g.roundRect(x, y, cw, 30, 15) : g.rect(x, y, cw, 30); g.fill(); g.stroke();
        g.fillStyle = '#a5f3fc'; g.fillText(tx, x + 10, y + 7);
      }
      boxes.push({ x, y, w: cw, h: 30, type: 'chip', i: c.i });
      x += cw + 8;
    }
    y += 40;
  }
  // rows
  const KW = 150;
  model.rows.forEach((r, ri) => {
    if (r.h) {
      y += 6;
      font(13, 700);
      if (draw) { g.fillStyle = '#67e8f9'; g.fillText(String(r.h).toUpperCase(), P, y); }
      y += 20;
      return;
    }
    let pw = 0;
    if (r.plate) { font(13, 800); pw = g.measureText(r.plate.text).width + 12; }
    font(16, 400);
    const tap = !!r.tap;
    const kw = tap ? w - 2 * P - 110 : KW;
    const vx = tap ? w - P - 104 : P + KW + (r.sw && r.sw.length ? 26 : 0) + (r.mtb ? 22 : 0) + (pw ? pw + 8 : 0);
    const vw = tap ? 104 : w - P - vx;
    const kl = wrap(r.k || '', kw - 8);
    const vl = wrap(r.v || '', vw);
    const lh = 21, rh = Math.max(kl.length, vl.length) * lh + 4;
    if (draw) {
      if (tap) { g.fillStyle = 'rgba(255,255,255,0.05)'; g.fillRect(P - 6, y - 2, w - 2 * P + 12, rh); }
      g.fillStyle = tap ? '#e8ecf4' : '#93a5c0';
      kl.forEach((l, i) => g.fillText(l, P, y + i * lh));
      g.fillStyle = r.dim ? '#6f85a3' : (tap ? '#a5f3fc' : '#e8ecf4');
      vl.forEach((l, i) => g.fillText(l, vx, y + i * lh));
      if (r.mtb) {
        g.beginPath(); g.arc(P + KW + 9, y + 9, 7, 0, Math.PI * 2);
        g.fillStyle = r.mtb; g.fill(); g.strokeStyle = 'rgba(0,0,0,0.6)'; g.lineWidth = 1; g.stroke();
      }
      if (r.plate) {
        font(13, 800);
        g.fillStyle = r.plate.col[0]; g.fillRect(P + KW, y, pw, 18);
        g.strokeStyle = r.plate.col[2]; g.lineWidth = 1.5; g.strokeRect(P + KW, y, pw, 18);
        g.fillStyle = r.plate.col[1]; g.fillText(r.plate.text, P + KW + 6, y + 2);
        font(16, 400);
      }
      if (r.sw && r.sw.length) {
        const sx = P + KW, n = r.sw.length;
        if (r.sw[0] === 'yellow') {
          g.save(); g.translate(sx + 9, y + 9); g.rotate(Math.PI / 4);
          g.fillStyle = MARK_COLOUR.yellow; g.fillRect(-6, -6, 12, 12); g.restore();
        } else {
          r.sw.forEach((b, i) => { g.fillStyle = MARK_COLOUR[b]; g.fillRect(sx, y + 1 + i * (16 / n), 18, 16 / n + 0.5); });
          g.strokeStyle = 'rgba(0,0,0,0.6)'; g.lineWidth = 1; g.strokeRect(sx, y + 1, 18, 16);
        }
      }
    }
    if (tap) boxes.push({ x: P - 6, y: y - 2, w: w - 2 * P + 12, h: rh, type: 'near', i: ri });
    y += rh;
  });
  // status
  if (model.status) {
    y += 6;
    font(16, 600);
    for (const l of wrap(model.status, w - 2 * P)) { if (draw) { g.fillStyle = '#ffc93c'; g.fillText(l, P, y); } y += 21; }
  }
  // actions: two per row
  y += 10;
  const bw = (w - 2 * P - 10) / 2, bh = 46;
  model.actions.forEach((a, i) => {
    const bx = P + (i % 2) * (bw + 10), by = y + Math.floor(i / 2) * (bh + 10);
    if (draw) {
      g.fillStyle = a.disabled ? 'rgba(255,255,255,0.04)' : 'rgba(103,232,249,0.14)';
      g.strokeStyle = a.disabled ? 'rgba(255,255,255,0.15)' : 'rgba(103,232,249,0.6)'; g.lineWidth = 1.5;
      g.beginPath(); g.roundRect ? g.roundRect(bx, by, bw, bh, 10) : g.rect(bx, by, bw, bh); g.fill(); g.stroke();
      font(17, 600);
      g.fillStyle = a.disabled ? '#6f85a3' : '#e8ecf4';
      g.fillText(`${a.icon} ${a.label}`, bx + 12, by + 13, bw - 20);
    }
    if (!a.disabled) boxes.push({ x: bx, y: by, w: bw, h: bh, type: 'action', i });
  });
  y += Math.ceil(model.actions.length / 2) * (bh + 10);
  // provenance
  font(12, 400);
  for (const l of wrap(model.provenance, w - 2 * P)) { if (draw) { g.fillStyle = '#6f85a3'; g.fillText(l, P, y); } y += 16; }
  y += P - 4;
  return { height: Math.ceil(y), boxes };
}

// ------------------------------------------------------------------- the UI
const LATCH_MS = 450;       // after a close, a trigger this soon is the same press
const VR_W = 0.55;          // plane width, m
const VR_D = 0.9;           // its distance ahead of the head, m (arm's length: the laser clicks it)
const VR_PX = 512;          // canvas width, px
const VR_MAX_PX = 1400;
const VR_MOVE_CLOSE_M = 4;

const _v1 = new THREE.Vector3(), _v2 = new THREE.Vector3();

// The phone's ◎ button / label (mobile.setInspect): lit iff the lead hit of
// the target under the centre dot is a specific thing (not open ground / sky /
// a still-measuring ray); icon + "Bench · 12 m". null = nothing there.
export function hintOf(target) {
  const lead = target && !target.pending && target.hits ? target.hits[0] : null;
  if (!lead || lead.kind === 'ground') return { lit: false, icon: '', label: '' };
  const d = Number.isFinite(target.dist) && !target.sky ? ` · ${fmtRange(target.dist)}` : '';
  return { lit: true, icon: iconOf(lead), label: `${titleOf(lead)}${d}` };
}

export class Inspect {
  // ctx: see main.js inspectCtx (getters onto the live world objects)
  constructor(ctx) {
    this.ctx = ctx;
    this.isOpen = false;
    this.target = null;
    this.model = null;
    this.idx = 0;
    this._calledNow = false;   // trigger() called this frame (level fire)
    this._held = false;        // ... and last frame (= still held)
    this._closedAt = -1e9;
    this._armed = false;       // the opening press has been released
    this._openMode = null;
    this._pre = false;         // opened on the Map before entry (no player; ⤳ Beam enters)
    this._origin = 'view';     // 'view' (the 3D view / VR) | 'map' (a Map tap, openAt)
    this._openCombat = false;  // combat at open (a Map card closes when it flips)
    this._mapAt = null;        // the Map tap: {pickR, client {x, y}}
    this._reg = null; this._regP = null; this._regState = 'idle';
    this._dom = null;
    this._vr = null;
    this._busy = false;
    this._closeTimer = 0;
    if (typeof document !== 'undefined') this._initDom();
  }

  // ---------------------------------------------------------- the trigger
  // Called every frame the trigger is DOWN (level); acts on the press edge.
  // Desktop / phone (VR opens through vrPoint, clicks through vrClick).
  trigger(now) {
    this._calledNow = true;
    if (this._held) return;
    if (this.isOpen) {
      this.close('outside');
      return;
    }
    if (now - this._closedAt < LATCH_MS) return;
    this.open();
  }

  // What a tap on the phone's ◎ button would open, through the SAME resolver
  // as open() (resolveTarget); null while it could not open (card up, combat
  // off the monocular, a throw). The caller throttles (main.js _inspProbe).
  probe() {
    const c = this.ctx, w = c.weapons;
    if (this.isOpen || !w || !w.monoActive || c.combatOn()) return null;
    try { return hintOf(this.resolveTarget('dwell')); } catch (e) { console.error('[inspect] probe', e); return null; }
  }

  // once per tick, after input: the latch, the dismiss rules, VR pointer
  frame(now) {
    this._held = this._calledNow;
    this._calledNow = false;
    if (!this.isOpen) return;
    const c = this.ctx, mode = c.mode();
    let why = null;
    // frame() runs only once a mode does (main.js): a card from the Map
    // before entry is done then (the beam closes it; this is the backstop)
    if (this._pre) { this.close('mode'); return; }
    if (this._origin === 'map') {
      // a Map card: no monocular / zoom / move rules — it lives with the Map
      if (mode !== this._openMode) why = 'mode';
      else if (!(c.mapOpen && c.mapOpen())) why = 'map';
      else if (!!c.combatOn() !== this._openCombat) why = 'combat';
      if (why) this.close(why);
      return;
    }
    if (mode !== this._openMode) why = 'mode';
    else if (c.mapOpen && c.mapOpen()) why = 'map';
    else if (c.combatOn()) why = 'combat';
    else if (!c.weapons || !c.weapons.monoActive) why = 'weapon';
    else if (c.dizzy && c.dizzy()) why = 'dizzy';
    else if (mode === 'vr' && c.player && this._openPos
      && Math.hypot(c.player.pos.x - this._openPos.x, c.player.pos.z - this._openPos.z) > VR_MOVE_CLOSE_M) why = 'moved';
    if (why) { this.close(why); return; }
    if (!this._armed && !(c.fireHeld && c.fireHeld())) this._armed = true;
    if (mode === 'flat' && this._dom && (now - (this._leaderAt || 0)) > 250) { this._leaderAt = now; this._leader(); }
  }

  // ---------------------------------------------------------- open/close
  open() {
    const c = this.ctx, mode = c.mode(), w = c.weapons;
    if (!w || !w.monoActive || c.combatOn()) return false;
    let target;
    try { target = this.resolveTarget(); } catch (e) { console.error('[inspect] target', e); return false; }
    if (!target) return false;
    this.target = target;
    this.idx = 0;
    this._openMode = mode;
    this._origin = 'view';
    this._armed = mode !== 'flat';
    this._openPos = c.player ? { x: c.player.pos.x, z: c.player.pos.z } : null;
    this._status = '';
    this.isOpen = true;
    w.monoFreeze = true;
    if (target.hits.some((h) => h.kind === 'building')) this._loadRegistry();
    this._render();
    this._present();
    if (c.sfx && c.sfx.ok) c.sfx.ok();
    this._telemetry('open');
    this._refine(target);
    return true;
  }

  // a Map tap / click at (x, z): the same card, no ray — P on the ground
  // (rangeGround), the eye = the player, resolveHits in flat2d (no y gates,
  // radii by the map's pick radius). opts: {pickR (m), poi (a tapped label's
  // gazetteer entry), name, kind, client {x, y} (desktop card placement)}.
  // Flat + phone only; combat may be on (beacon / beam then hidden).
  // BEFORE ENTRY (ctx.preEntry() = the launch tap's entry mode, ctx.mode()
  // null): the card of the Map before entry — the phone's layout iff the
  // entry mode is 'mobile', else the desktop's (VR: the headset browser's flat
  // page); no player, so nothing is measured from one; ⤳ Beam enters
  // (ctx.enterAt). Opening it prefetches the near ground there (ctx.prefetch).
  openAt(x, z, opts = {}) {
    const c = this.ctx;
    const pre = !c.mode() && c.preEntry ? c.preEntry() : null;
    const mode = c.mode() || (pre ? (pre === 'mobile' ? 'mobile' : 'flat') : null);
    if (mode !== 'flat' && mode !== 'mobile') return false;
    if (this.isOpen) this.close('retarget');
    let target;
    try { target = this._mapTarget(x, z, opts); } catch (e) { console.error('[inspect] map target', e); return false; }
    this.target = target;
    this.idx = 0;
    this._openMode = mode;
    this._pre = !!pre;
    this._origin = 'map';
    this._openCombat = !!c.combatOn();
    this._mapAt = { pickR: opts.pickR || 0, client: opts.client || null, poi: opts.poi || null, name: opts.name || '' };
    this._armed = true;
    this._openPos = null;
    this._status = '';
    this.isOpen = true;
    this._loadRegistry();        // map cards name buildings from the register (one shared fetch)
    this._render();
    this._present();
    this._mapSelect();
    this._telemetry('open');
    // R10: the near ground at the card's point starts loading now (main.js
    // loadRetarget, before entry only) — the ⤳ Beam then mostly finds it in
    if (pre && c.prefetch) c.prefetch(x, z);
    return true;
  }

  // ctx.player null = no player yet (before entry): eye null, no fallback
  _mapTarget(x, z, opts) {
    const c = this.ctx;
    const pp = c.player ? c.player.pos : null;
    const eye = pp ? { x: pp.x, y: pp.y + 1.6, z: pp.z } : null;
    const y = (c.rangeGround ? c.rangeGround(x, z) : null) ?? (pp ? pp.y : 0);
    const P = { x, y, z };
    let hits = resolveHits(c, P, { flat2d: true, pickR: opts.pickR || 16, reg: this._reg, zoom: 1, mppCss: opts.mppCss ?? null });
    // a tapped label leads with what it names: a route shield / name → the
    // route card, an amenity icon → its card, a grade chip / trail name → the path
    const ent = opts.ent || null;
    if (ent && ent.kind === 'route') {
      const re = routeEnt(c, ent.ri, x, z);
      if (re) hits.unshift(re);
    } else if (ent && ent.kind === 'amenity') {
      const i = hits.findIndex((h) => h.kind === 'amenity' && h.data.ai === ent.ai);
      if (i > 0) hits.unshift(hits.splice(i, 1)[0]);
      else if (i < 0 && c.amenities && c.amenities[ent.ai]) {
        const p = c.amenities[ent.ai];
        hits.unshift({ kind: 'amenity', name: p.name || '', x: p.x, y: p.y ?? y, z: p.z, data: { am: p, ai: ent.ai } });
      }
    } else if (ent && ent.kind === 'trail') {
      const i = hits.findIndex((h) => h.kind === 'trail');
      if (i > 0) hits.unshift(hits.splice(i, 1)[0]);
    } else if (opts.poi || opts.name) {
      const i = hits.findIndex((h) => opts.poi ? h.data && h.data.poi && h.data.poi.name === opts.poi.name && h.data.poi.kind === opts.poi.kind
        : h.name === opts.name);
      if (i > 0) hits.unshift(hits.splice(i, 1)[0]);
      else if (i < 0 && opts.poi) {
        const p = opts.poi;
        hits.unshift({ kind: gazKind(p.kind), name: p.name, x: p.x, y: p.ele ?? y, z: p.z,
          data: { poi: { name: p.name, kind: p.kind, ele: p.ele, prom: p.prom } } });
      }
    }
    // your own station (its sign rows = actions) leads when tapped; never the
    // "Riding" lead — the tap is about the place on the map
    if (!eye) return { eye: null, dir: null, P, dist: null, src: 'map', hits, sky: false };
    if (!(c.zones && c.zones.riding)) hits = stationLead(c, hits, eye);
    const dx = x - eye.x, dz = z - eye.z, L = Math.hypot(dx, dz) || 1;
    return { eye, dir: { x: dx / L, y: 0, z: dz / L }, P, dist: L, src: 'map', hits, sky: false };
  }

  // the Map's ring on the card's target (null = clear); a route card
  // highlights its route (the others dim) and fits it (its stage after a
  // stage step: fitStage) — only when the route / stage changes
  _mapSelect(fitStage = false) {
    const c = this.ctx;
    const e = this.isOpen && this._origin === 'map' && this.model ? this.model.ent : null;
    if (c.mapSelect) c.mapSelect(e && e.kind !== 'route' ? { x: e.x, z: e.z } : null);
    if (!c.mapRoute) return;
    const key = e && e.kind === 'route' ? `${e.data.ri}|${e.data.si}` : null;
    if (key === this._hlKey) return;
    const was = this._hlKey;
    this._hlKey = key;
    if (!key) { c.mapRoute(null, -1); return; }
    const top = this._openMode === 'mobile' && this._dom ? this._dom.card.getBoundingClientRect().top : null;
    const sameRoute = !!was && was.split('|')[0] === String(e.data.ri);
    // opening: the whole route in view; a stage step: that stage
    c.mapRoute(e.data.ri, e.data.si, { fit: fitStage || sameRoute ? 'stage' : 'route', top });
  }

  get origin() { return this.isOpen ? this._origin : null; }

  // the building register (null until loaded; the first call starts the one
  // shared fetch) — the Map's building names / house numbers
  registry() {
    if (!this._regP && this.ctx.baseUrl) this._loadRegistry();
    return this._reg;
  }

  close(why = '') {
    if (!this.isOpen) return;
    this._chargeEnd(false);
    this.isOpen = false;
    this._closedAt = performance.now();
    clearTimeout(this._closeTimer);
    const c = this.ctx;
    if (c.weapons) c.weapons.monoFreeze = false;
    if (this._dom) {
      this._dom.root.hidden = true;
      this._dom.root.classList.remove('sheet');
    }
    if (this._vr) this._vr.mesh.visible = false;
    if (this._dom) this._dom.root.classList.remove('map');
    const fromMap = this._origin === 'map';
    this._origin = 'view';
    if (fromMap && c.mapSelect) c.mapSelect(null);
    if (this._hlKey && c.mapRoute) c.mapRoute(null, -1);
    this._hlKey = null;
    // flat: give the mouse back to the game (a close from a mouse press is a
    // user gesture; Esc is not one — then the next canvas click re-locks).
    // A Map card leaves the mouse to the Map (👁 Look re-locks in main.js).
    if (!fromMap && this._openMode === 'flat' && why !== 'map' && why !== 'mode' && c.canvas
      && document.pointerLockElement !== c.canvas) {
      try { const p = c.canvas.requestPointerLock(); if (p && p.catch) p.catch(() => {}); } catch { /* not allowed now */ }
    }
    this._openMode = null;
    this._pre = false;
    this.target = null; this.model = null;
  }

  // ---------------------------------------------------------- resolution
  // ctx.view() → {cam, band, opticZoom}: the camera the screen shows. The
  // optic band (and VR, no view()) reads the optic's depth; below the band
  // there is no depth target, so a building step test runs first.
  resolveTarget(req = 'now') {   // req: the aim ray's tile request ('dwell' for the phone's hint probe)
    const c = this.ctx, w = c.weapons;
    const v = c.view ? c.view() : null;
    const optic = !v || v.band;
    const cam = optic ? w.monoCam : v.cam;
    cam.updateMatrixWorld(true);
    const eye = cam.getWorldPosition(new THREE.Vector3());
    const dir = cam.getWorldDirection(new THREE.Vector3());
    let d = null, src = 'depth';
    if (optic) d = w.depthAt ? w.depthAt(512, 320) : null;
    else {
      const b = c.buildingHit ? c.buildingHit(eye, dir, 300) : null;
      if (b) { d = b.dist; src = 'march'; }
    }
    let hit = null;
    if (d === null || d === undefined || !Number.isFinite(d)) {
      // the aim ray (main.js _rayMarch → aimray.js): dist null + final =
      // open sky; !final = the true ground there is still loading
      hit = c.rayMarch ? c.rayMarch(eye, dir, req) : null;
      d = null; src = 'march';
    }
    const zoom = optic ? w.monoZoom : (v.opticZoom || 1);
    return this._targetOn({ x: eye.x, y: eye.y, z: eye.z }, { x: dir.x, y: dir.y, z: dir.z }, d, src, hit, zoom);
  }

  // the target on one ray: the distance d (depth / building) or the march
  // result `hit`. A non-final march (AIM-SKY: an undecided stretch of the
  // ray over the coarse envelope, its L0 tiles in flight) is `pending`: the
  // card says so and _refine re-targets once the ground is in. Open sky is
  // only a FINAL miss.
  _targetOn(eye, dir, d, src, hit, zoom, labels0) {
    const c = this.ctx;
    let pending = false, bound = '';
    if (hit !== null && hit !== undefined) {
      d = hit.dist;
      pending = hit.final === false;
      if (pending) bound = d !== null ? '≤ ' : '≥ ';
      // no hit yet on an undecided ray: the card stands at the first
      // undecided stretch (the ground is at or beyond it), flagged pending
      if (d === null && pending) d = hit.undecided ?? null;
    }
    const sky = d === null || d === undefined;
    if (sky) { d = 20000; src = 'sky'; }
    const P = { x: eye.x + dir.x * d, y: eye.y + dir.y * d, z: eye.z + dir.z * d };
    let labels = labels0 || null;
    if (!labels && (sky || d > FAR_M) && c.viewLabels) {
      try {
        const l = c.viewLabels();
        labels = Array.isArray(l) ? l : (l && l.features) || null;
      } catch (e) { console.error('[inspect] labels', e); }
    }
    const ray = { ox: eye.x, oy: eye.y, oz: eye.z, dx: dir.x, dy: dir.y, dz: dir.z };
    // undecided with no hit yet: P is a point of the ray in the air, not a
    // place — the card is the one "measuring" ground row until _refine lands
    const hits = pending && bound === '≥ '
      ? [{ kind: 'ground', name: '', x: P.x, y: P.y, z: P.z, data: { sky: false, pending: true, undecided: true } }]
      : stationLead(c, resolveHits(c, P, { zoom, sky, dist: d, labels, reg: this._reg, ray }), eye);
    if (pending) {
      const g = hits.find((h) => h.kind === 'ground');
      if (g) g.data.pending = true;
    }
    return { eye, dir, P, dist: d, src, hits, sky, pending, bound, zoom, labels };
  }

  // a pending target: fetch the ray's L0 (ctx.aimRefine) and re-target on
  // it while the same card is still open. The lead stays the kind it was
  // when that kind is still hit, else the new first hit leads.
  _refine(t) {
    const c = this.ctx;
    if (!t || !t.pending || !c.aimRefine) return;
    c.aimRefine(t.eye, t.dir).then((hit) => {
      if (!this.isOpen || this.target !== t || !hit) return;
      const lead = this.model ? this.model.ent : null;
      let nt;
      try { nt = this._targetOn(t.eye, t.dir, null, 'march', hit, t.zoom, t.labels); } catch (e) { console.error('[inspect] refine', e); return; }
      this.target = nt;
      const i = lead ? nt.hits.findIndex((h) => h.kind === lead.kind && h.name === lead.name && lead.kind !== 'ground') : -1;
      this.idx = i >= 0 ? i : 0;
      if (nt.hits.some((h) => h.kind === 'building')) this._loadRegistry();
      this._render(); this._present();
      // still not final (a tile failed): leave the card provisional
    }, (e) => console.error('[inspect] refine', e));
  }

  _loadRegistry() {
    if (this._regP) return this._regP;
    const c = this.ctx;
    this._regState = 'loading';
    // the asset queue's map class: the Map's building names wait on it
    this._regP = fetchAsset(`${c.baseUrl}/buildings/registry.json`, { prio: 'map', as: 'json' })
      .catch(() => null)
      .then((reg) => {
        this._reg = reg && reg.buildings ? reg : null;
        this._regState = this._reg ? 'ready' : 'absent';
        if (!this._reg) infoOnce('reg', '[inspect] buildings/registry.json unavailable — building cards show "no address on file"');
        // a Map card: re-resolve with the register (far buildings are only in it)
        if (this.isOpen && this.target && this._origin === 'map' && this._reg && this.target.src === 'map') {
          const m = this._mapAt || {};
          const lead = this.model ? this.model.ent : null;
          try {
            this.target = this._mapTarget(this.target.P.x, this.target.P.z, m);
            if (lead) {
              const i = this.target.hits.findIndex((h) => h.kind === lead.kind && h.name === lead.name);
              this.idx = i >= 0 ? i : 0;
              if (lead.kind === 'ground' && this.target.hits[0].kind === 'building') this.idx = 0;
            }
          } catch (e) { console.error('[inspect] map target', e); }
          this._render(); this._present(); this._mapSelect();
          return this._reg;
        }
        // the open card was built without it: attach the record + repaint
        if (this.isOpen && this.target) {
          for (const h of this.target.hits) {
            if (h.kind !== 'building' || h.data.rec || !h.data.item) continue;
            const { rec, how } = registryRecord(this._reg, h.data.item);
            h.data.rec = rec; h.data.how = how;
            if (rec) {
              h.name = rec.name || (rec.osm && rec.osm.name) || (rec.gwr && rec.gwr.label) || h.name;
              if (typeof rec.x === 'number') { h.x = rec.x; h.z = rec.z; }
            }
          }
          this._render(); this._present();
        }
        return this._reg;
      });
    return this._regP;
  }

  // ---------------------------------------------------------- the model
  _render() {
    this.model = buildCard(this.ctx, this.target, this.idx, this);
    if (this._status) this.model.status = this._status;
  }

  _telemetry(action) {
    const c = this.ctx, m = this.model;
    if (!c.net || !c.net.inspect || !m) return;
    const d = m.head.dist;
    c.net.inspect({ kind: TEL_KIND[m.kind] || m.kind, had: m.had ? 1 : 0, action: TEL_ACTION[action] || action,
      dist: Number.isFinite(d) ? Math.round(d) : 0 });
  }

  // a box from a presenter: action / chip / near row
  _activate(box) {
    if (!this.isOpen || !this.model) return;
    const c = this.ctx;
    if (box.type === 'chip') {
      this.idx = box.i; this._status = '';
      this._render(); this._present(); this._mapSelect();
      this._telemetry('chip');
      return;
    }
    if (box.type === 'near') {
      const r = this.model.rows[box.i];
      if (!r || !r.tap) return;
      this._retarget(r.tap);
      this._telemetry('near');
      return;
    }
    const a = this.model.actions[box.i];
    if (!a || a.disabled) return;
    const id = a.id;
    this._telemetry(id);
    let res;
    try { res = a.run({ ctx: c, target: this.target, ent: this.model.ent, ui: this }); } catch (e) {
      console.error('[inspect action]', id, e); res = t('player.insp.res.failed');
    }
    if (!this.isOpen) return;                       // the action closed the card
    const show = (s) => {
      if (!this.isOpen) return;
      this._status = s || '';
      this._render(); this._present();
    };
    if (res && typeof res.then === 'function') { show('…'); res.then(show, () => show(t('player.insp.res.failed'))); } else show(res);
  }

  // re-target on a "what's near" entry: that POI leads, the ground under it follows
  _retarget(ent) {
    const c = this.ctx;
    const y = ent.y ?? (c.rangeGround ? c.rangeGround(ent.x, ent.z) : null) ?? this.target.P.y;
    const e = { ...ent, y };
    const ground = { kind: 'ground', name: '', x: ent.x, y, z: ent.z, data: { sky: false } };
    this.target = { ...this.target, P: { x: ent.x, y, z: ent.z }, hits: [e, ground], src: 'gazetteer' };
    this.idx = 0; this._status = '';
    this._render(); this._present(); this._mapSelect();
  }

  // where "beam to there" seats you: the entity's point, stepped back toward
  // the eye out of a building footprint / a lake — with no eye (before entry)
  // out along the shortest way to free ground (16 bearings, 1 m rings, the
  // nearest free point wins); null = outside the map
  _beamPoint(e) {
    const c = this.ctx;
    const ex = c.hf ? c.hf.extentX : Infinity, ez = c.hf ? c.hf.extentZ : Infinity;
    let x = e.x, z = e.z;
    if (e.kind === 'station' && e.data.zone && e.data.zone.beam) { x = e.data.zone.beam[0]; z = e.data.zone.beam[2]; }
    const eye = this.target ? this.target.eye : null;
    const blocked = (px, pz) => (e.kind === 'building' && c.collider && c.collider.insideFootprint(px, pz))
      || (c.water && c.water.surfaceAt && c.water.surfaceAt(px, pz) !== null);
    if (eye && blocked(x, z)) {
      const L = Math.hypot(eye.x - x, eye.z - z) || 1;
      const ux = (eye.x - x) / L, uz = (eye.z - z) / L;
      let s = 1;
      for (; s < Math.min(L, 400); s += 1) if (!blocked(x + ux * s, z + uz * s)) break;
      x += ux * (s + 2); z += uz * (s + 2);
    } else if (!eye && blocked(x, z)) {
      out: for (let s = 1; s < 400; s += 1) {
        for (let k = 0; k < 16; k++) {
          const a = k * Math.PI / 8, ux = Math.sin(a), uz = -Math.cos(a);
          if (blocked(x + ux * s, z + uz * s)) continue;
          x += ux * (s + 2); z += uz * (s + 2);
          break out;
        }
      }
    }
    if (!(x >= 2 && z >= 2 && x <= ex - 2 && z <= ez - 2)) return null;
    return { x, z };
  }

  // the charge's end: launch = the beam's action (at full: the release's
  // gesture, else the frame it filled — the press's activation still holds),
  // else cancelled (the column goes, the button is re-rendered)
  _chargeEnd(launch) {
    const ch = this._charge;
    if (!ch) return;
    this._charge = null;
    cancelAnimationFrame(ch.raf);
    const go = ch.h.stop(launch);
    if (go) this._activate({ type: 'action', i: ch.i });
    else if (this.isOpen && this.model) this._render();
  }

  // Before entry the beam IS the entry: ctx.enterAt (main.js) does the
  // gesture-bound part synchronously — this runs inside the click — then
  // places and starts the world; it resolves a status line for the card
  // (refused / XR denied) or closes it (entered).
  _beam(bp) {
    const c = this.ctx;
    if (this._pre && c.enterAt) {
      if (this._busy) return t('player.insp.res.beaming');
      this._busy = true;
      const label = this.model ? titleOf(this.model.ent) : '';
      return Promise.resolve(c.enterAt(bp.x, bp.z, { label })).then((r) => { this._busy = false; return r; },
        () => { this._busy = false; return t('player.insp.res.failed'); });
    }
    if (c.combatOn()) return t('player.insp.res.refusedCombat');
    if (this._busy) return t('player.insp.res.beaming');
    this._busy = true;
    // a Map card's beam always lands in the 3D view: the Map (and with it
    // this card) closes inside the click, so desktop gets the mouse back;
    // a server refusal then only plays the deny sound
    const fromMap = this._origin === 'map' && c.closeMap;
    if (fromMap) c.closeMap();
    return c.placeAt(bp.x, bp.z).then((pos) => {
      this._busy = false;
      if (!pos) {
        if (c.sfx && c.sfx.deny) c.sfx.deny();
        return t('player.insp.res.refusedServer');
      }
      if (c.sfx && c.sfx.ok) c.sfx.ok();
      clearTimeout(this._closeTimer);
      if (!fromMap) this._closeTimer = setTimeout(() => this.close('beamed'), 1400);
      return t('player.insp.res.beamed', { h: fmtInt(pos[1]) });
    }, () => { this._busy = false; return t('player.insp.res.noReply'); });
  }

  // ---------------------------------------------------------- presenters
  _present() {
    if (!this.model) return;
    if (this._openMode === 'vr') this._presentVr();
    else this._presentDom();
  }

  _initDom() {
    const root = document.getElementById('inspect');
    if (!root) return;
    const card = document.getElementById('inspect-card');
    const d = this._dom = { root, card, line: document.getElementById('inspect-leader-line'),
      dot: document.getElementById('inspect-leader-dot') };
    const inCard = (t) => t && card.contains(t);
    // a Map card: the Map (its canvas and buttons) keeps its own input — a
    // drag pans under the card, a tap goes to the Map (which closes the card:
    // main.js onTapWorld), the buttons work
    const pmap = document.getElementById('pmap-full');
    const onMap = (t) => this._origin === 'map' && pmap && t && pmap.contains(t);
    // press outside = close (any button / touch), once the opening press is up
    document.addEventListener('pointerdown', (e) => {
      if (!this.isOpen || this._openMode === 'vr' || inCard(e.target) || onMap(e.target)) return;
      if (!this._armed) return;
      this.close('outside');
    }, true);
    // the click that tails a press on the canvas would re-request pointer
    // lock (FlatControls) while the card is up — swallow it there
    window.addEventListener('click', (e) => {
      if (!this.isOpen || this._openMode === 'vr' || inCard(e.target) || onMap(e.target)) return;
      e.stopPropagation(); e.preventDefault();
    }, true);
    document.addEventListener('contextmenu', (e) => {
      if (this.isOpen || performance.now() - this._closedAt < 800) e.preventDefault();
    }, true);
    document.addEventListener('keydown', (e) => {
      if (this._charge && e.code === 'Escape') { this._chargeEnd(chargeAct(0, 'esc') === 'launch'); return; }   // Esc cancels the charge, the card stays
      if (this.isOpen && e.code === 'Escape' && this._openMode !== 'vr') this.close('esc');
    });
    // THE CHARGE (PC, beamcharge.js; owner 2026-10-31 "it must be super
    // intuitive"): the ⤳ Beam pressed (any pointer) or clicked from the
    // keyboard starts it — main.js beamCharge preloads the landing — and it
    // launches itself when full (charge_ms), held or not; Esc or a second
    // press on the button cancels. A null handle (phone, VR, a beam running)
    // keeps the click
    const charge = (el, i) => {
      const a = this.model && this.model.actions[i];
      if (!a || a.id !== 'beam' || !a.bp || el.disabled || !this.ctx.beamCharge || this._busy) return false;
      const h = this.ctx.beamCharge(a.bp);
      if (!h) return false;
      this._charge = { h, i };
      const tick = () => {
        const ch = this._charge;
        if (!ch) return;
        const f = ch.h.fill();
        if (chargeAct(f, 'tick') === 'launch') { this._chargeEnd(true); return; }
        const b = card.querySelector(`button[data-t="action"][data-i="${ch.i}"]`);
        if (b) {
          b.classList.add('charging');
          b.style.setProperty('--fill', f.toFixed(3));
          const txt = `⤳ ${t('player.insp.act.beamCharging')}${ch.h.dist ? ` · ${ch.h.dist}` : ''} · ${t('player.insp.act.beamEsc')}`;
          if (b.textContent !== txt) b.textContent = txt;
        }
        ch.raf = requestAnimationFrame(tick);
      };
      tick();
      return true;
    };
    card.addEventListener('pointerdown', (e) => {
      this._noClick = false;
      const el = e.target.closest('button[data-t="action"]');
      if (!el || e.button !== 0) return;
      if (this._charge) {                     // a second press on the button: cancelled
        if (this._charge.i === Number(el.dataset.i)) { this._noClick = true; this._chargeEnd(chargeAct(0, 'again') === 'launch'); }
        return;
      }
      if (charge(el, Number(el.dataset.i))) this._noClick = true;   // the click this press ends in is the charge's, not a beam
    });
    window.addEventListener('pointerup', () => {
      if (this._charge && chargeAct(this._charge.h.fill(), 'release') === 'launch') this._chargeEnd(true);
      if (this._noClick) setTimeout(() => { this._noClick = false; }, 0);   // only the click THIS press ends in is swallowed
    }, true);
    card.addEventListener('click', (e) => {
      const el = e.target.closest('[data-t]');
      if (!el) return;
      if (this._noClick) { this._noClick = false; e.preventDefault(); return; }
      e.preventDefault();
      if (el.dataset.t === 'action' && (this._charge || charge(el, Number(el.dataset.i)))) return;   // a keyboard click: the same charge
      this._activate({ type: el.dataset.t, i: Number(el.dataset.i) });
    });
    // sheet: swipe down closes
    let sy = null;
    card.addEventListener('touchstart', (e) => { sy = card.scrollTop <= 0 ? e.touches[0].clientY : null; }, { passive: true });
    card.addEventListener('touchmove', (e) => {
      if (sy !== null && e.touches[0].clientY - sy > 70) { sy = null; this.close('swipe'); }
    }, { passive: true });
    card.addEventListener('touchend', () => { sy = null; }, { passive: true });
  }

  _presentDom() {
    const d = this._dom, m = this.model;
    if (!d) return;
    const mob = this._openMode === 'mobile';
    const map = this._origin === 'map';
    d.root.classList.toggle('sheet', mob);
    d.root.classList.toggle('map', map);
    let h = `<div class="ic-grab"></div><div class="ic-head"><span class="ic-icon">${esc(m.icon)}</span>`
      + `<div><div class="ic-title">${esc(m.title)}</div><div class="ic-sub">${esc(m.sub)}</div></div></div>`;
    if (m.chips.length) {
      h += `<div class="ic-chips"><span>${esc(t('player.insp.alsoHere'))}</span>`
        + m.chips.map((c) => `<button data-t="chip" data-i="${c.i}">${esc(c.icon)} ${esc(c.label)}</button>`).join('') + '</div>';
    }
    h += '<div class="ic-rows">';
    m.rows.forEach((r, i) => {
      if (r.h) { h += `<div class="ic-h">${esc(r.h)}</div>`; return; }
      const sw = r.sw && r.sw.length
        ? `<span class="tm-sw${r.sw[0] === 'yellow' ? ' tm-d' : ''}">${r.sw.map((b) => `<i class="tm-${b}"></i>`).join('')}</span> ` : '';
      const mtb = r.mtb ? `<span class="tm-mtb" style="background:${esc(r.mtb)}"></span> ` : '';
      const pl = r.plate ? `<span class="ic-plate" style="background:${esc(r.plate.col[0])};color:${esc(r.plate.col[1])};border-color:${esc(r.plate.col[2])}">${esc(r.plate.text)}</span> ` : '';
      const v = r.href ? `<a href="${esc(r.href)}" target="_blank" rel="noopener">${esc(r.v)}</a>` : esc(r.v);
      if (r.tap) h += `<button class="ic-row ic-near" data-t="near" data-i="${i}"><span class="k">${esc(r.k)}</span><span class="v">${esc(r.v)}</span></button>`;
      else h += `<div class="ic-row${r.dim ? ' dim' : ''}"><span class="k">${esc(r.k || '')}</span><span class="v">${sw}${mtb}${pl}${v}</span></div>`;
    });
    h += '</div>';
    if (m.status) h += `<div class="ic-status">${esc(m.status)}</div>`;
    h += '<div class="ic-actions">' + m.actions.map((a, i) => `<button data-t="action" data-i="${i}"${a.disabled ? ' disabled' : ''}>${esc(a.icon)} ${esc(a.label)}</button>`).join('') + '</div>';
    h += `<div class="ic-prov">${esc(m.provenance)}</div>`;
    d.card.innerHTML = h;
    if (d.root.hidden) {
      d.root.hidden = false;
      d.card.scrollTop = 0;
      // flat: the mouse is the card's now (the optic stays up, frozen)
      if (!mob && document.pointerLockElement) document.exitPointerLock();
    }
    if (map) { this._placeMapCard(mob); return; }
    d.card.style.left = ''; d.card.style.top = '';
    this._leaderAt = 0;
    if (!mob) this._leader();
  }

  // a Map card: desktop — beside the tapped point (right, else left; clamped
  // to the window); phone — the sheet, the Map panned so the point shows above it
  _placeMapCard(mob) {
    const d = this._dom, card = d.card, m = this._mapAt || {};
    if (mob) {
      card.style.left = ''; card.style.top = '';
      const e = this.model && this.model.ent, c = this.ctx;
      if (e && e.kind !== 'route' && c.mapReveal) c.mapReveal(e.x, e.z, card.getBoundingClientRect().top);
      return;
    }
    const p = m.client || { x: innerWidth / 2, y: innerHeight / 2 };
    const w = card.offsetWidth || 340, h = card.offsetHeight || 300, gap = 22, edge = 12;
    let x = p.x + gap;
    if (x + w > innerWidth - 64) x = p.x - gap - w;            // clear of the zoom buttons on the right
    x = Math.max(edge, Math.min(innerWidth - w - edge, x));
    const y = Math.max(64, Math.min(innerHeight - h - edge, p.y - h * 0.35));
    card.style.left = `${Math.round(x)}px`; card.style.top = `${Math.round(y)}px`;
  }

  // desktop: a thin line from the reticle (screen centre) to the card
  _leader() {
    const d = this._dom;
    if (!d || !d.line || this._openMode !== 'flat' || this._origin === 'map') return;
    const r = d.card.getBoundingClientRect();
    const cx = innerWidth / 2, cy = innerHeight / 2;
    const ty = Math.max(r.top + 18, Math.min(r.bottom - 18, cy));
    d.line.setAttribute('x1', cx + 9); d.line.setAttribute('y1', cy);
    d.line.setAttribute('x2', r.left); d.line.setAttribute('y2', ty);
    d.dot.setAttribute('cx', cx); d.dot.setAttribute('cy', cy);
  }

  // VR: a world plane VR_D ahead, 0.22 m right of the view axis, facing the
  // head; the SAME model painted by paintCard; clicked by the vrui laser
  _ensureVr() {
    if (this._vr) return this._vr;
    const canvas = document.createElement('canvas');
    canvas.width = VR_PX; canvas.height = 512;
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false,
      toneMapped: false, fog: false, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    mesh.renderOrder = 1001; mesh.frustumCulled = false; mesh.visible = false;
    mesh.name = 'inspect-card';
    this.ctx.scene.add(mesh);
    this._vr = { canvas, tex, mesh, boxes: [], hpx: 512 };
    return this._vr;
  }

  _presentVr() {
    const v = this._ensureVr(), g = v.canvas.getContext('2d');
    const { height } = paintCard(g, this.model, VR_PX, 0);
    const H = Math.min(VR_MAX_PX, height);
    if (v.canvas.height !== H) v.canvas.height = H;
    const out = paintCard(v.canvas.getContext('2d'), this.model, VR_PX, H);
    v.boxes = out.boxes; v.hpx = H;
    v.tex.needsUpdate = true;
    v.mesh.scale.set(VR_W, VR_W * H / VR_PX, 1);
    if (!v.mesh.visible) {
      const head = this.ctx.xrCamera();
      head.getWorldPosition(_v1);
      head.getWorldDirection(_v2);
      _v2.y = 0;
      if (_v2.lengthSq() < 1e-4) _v2.set(0, 0, -1);
      _v2.normalize();
      const right = new THREE.Vector3(-_v2.z, 0, _v2.x);   // fwd × up
      v.mesh.position.copy(_v1).addScaledVector(_v2, VR_D).addScaledVector(right, 0.22);
      v.mesh.position.y -= 0.08;
      v.mesh.lookAt(_v1);
      v.mesh.visible = true;
    }
  }

  // the VR card as a laser target of vrui.js (its 1 × 1 plane, scaled; the
  // canvas px) and its click there — the vrui laser is the card's pointer
  get vrCard() {
    const v = this._vr;
    return v && v.mesh.visible && this.isOpen ? { mesh: v.mesh, wPx: VR_PX, hPx: v.hpx } : null;
  }

  vrClick(px, py) {
    const v = this._vr;
    if (!v || !this.isOpen) return;
    const box = v.boxes.find((b) => px >= b.x && px <= b.x + b.w && py >= b.y && py <= b.y + b.h);
    if (box) this._activate(box);
  }
}
